// src/pages/instances/reports/InstanceCommissionsReport.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SettingsIcon from "@mui/icons-material/Settings";
import { useHistory, useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { UIDataGridTable, UITableShell, UITableToolbar } from "../../../../components/ui";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  fetchCommissionsReport,
  updateCommissionOverride,
  paySelectedCommissions,
  payCommissionsExportXlsx,
  fetchCommissionReadinessPolicy,
  updateCommissionReadinessPolicy,
  unpayCommissions,
} from "../../../../services/reports";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import ReportSummaryCard from "../shared/ReportSummaryCard";
import { parseSavedBoolean, normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportShellSx,
  reportSummaryGridSx,
} from "../shared/reportLayout";

const currency = (n) => `CA$${(Number(n) || 0).toFixed(2)}`;
const toNumber = (v) => (v == null || v === "" ? null : Number(v));
const percent = (x, decimals = 2) => `${((Number(x) || 0) * 100).toFixed(decimals)}%`;
const isManualFlatRow = (row = {}) =>
  String(row?.entryType || "").toLowerCase() === "manual" &&
  (row?.isManualCommission || row?.manualCommissionMode === "flat" || row?.payoutAmountSource === "manual_flat");
const getSalesOrderRouteId = (row = {}) =>
  row?.orderNumber ||
  row?.OrderId ||
  row?.salesOrderId ||
  row?.orderPublicId ||
  row?.orderId;

const DEFAULT_FILTERS = Object.freeze({
  dateFrom: "",
  dateTo: "",
  repId: "",
  repType: "client",
  paidStatus: "",
  state: "",
  invoiceStatus: "both",
  billStatus: "both",
  commissionStatus: "both",
  client: "",
  invoiceDatePaidFrom: "",
  invoiceDatePaidTo: "",
  commissionDatePaidFrom: "",
  commissionDatePaidTo: "",
  quickFind: "",
  readyFilter: "all",
  showMoreFilters: false,
});

// ─────────────────────────────────────────────
// Helpers copied from Purchasing (normalized)
// ─────────────────────────────────────────────
const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    const selectedUrl = selectedPic?.url;
    if (!selectedUrl || typeof selectedUrl !== "string") return null;
    if (/^https?:\/\//i.test(selectedUrl)) return selectedUrl;
    if (selectedUrl.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${selectedUrl}`;
    if (selectedUrl.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${selectedUrl}`;
    return selectedUrl;
  }
  if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
  if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
  if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
  if (typeof imgSrc === "string") {
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      // noop - keep raw fallback below
    }
  }
  return imgSrc;
};

const buildUserIndex = (users = []) => {
  const byId = new Map();
  (users || []).forEach((u) => {
    if (u?.id == null) return;
    const first = u.first_name || u.firstName || "";
    const last = u.last_name || u.lastName || "";
    const email = u.email || "";
    const name = `${first} ${last}`.trim() || email || `User #${u.id}`;
    const pic = u.profilePicture || u.avatar || u.photoUrl || null;
    byId.set(String(u.id), {
      id: String(u.id),
      name,
      first_name: first,
      last_name: last,
      email,
      profilePicture: normalizeAvatarUrl(pic),
      role: u.role || u.userRole || "",
    });
  });
  return byId;
};

const asUserShape = (uLike, userIdx) => {
  if (uLike && typeof uLike === "object" && uLike.id != null) {
    const fromIdx = userIdx?.get(String(uLike.id));
    return {
      id: String(uLike.id),
      first_name: uLike.first_name || uLike.firstName || fromIdx?.first_name || "",
      last_name: uLike.last_name || uLike.lastName || fromIdx?.last_name || "",
      email: uLike.email || fromIdx?.email || "",
      role: uLike.role || uLike.userRole || fromIdx?.role || "",
      profilePicture: normalizeAvatarUrl(uLike.profilePicture || uLike.avatar || uLike.photoUrl || fromIdx?.profilePicture || null),
    };
  }
  // Already normalized user object
  if (uLike && (uLike.email || uLike.first_name || uLike.last_name || uLike.profilePicture)) {
    return {
      id: uLike.id != null ? String(uLike.id) : undefined,
      first_name: uLike.first_name || uLike.firstName || "",
      last_name: uLike.last_name || uLike.lastName || "",
      email: uLike.email || "",
      role: uLike.role || uLike.userRole || "",
      profilePicture: normalizeAvatarUrl(uLike.profilePicture || uLike.avatar || uLike.photoUrl),
    };
  }

  // ID → lookup
  const idStr =
    typeof uLike === "number" || (typeof uLike === "string" && /^\d+$/.test(uLike)) ? String(uLike) : null;
  if (idStr && userIdx?.get(idStr)) {
    const u = userIdx.get(idStr);
    return {
      id: u.id,
      first_name: u.first_name || (u.name || "").split(" ")[0] || "",
      last_name: u.last_name || (u.name || "").split(" ").slice(1).join(" ") || "",
      email: u.email || "",
      role: u.role || "",
      profilePicture: u.profilePicture || null,
    };
  }

  // Email → pseudo user
  if (typeof uLike === "string" && /@/.test(uLike)) {
    return {
      id: undefined,
      first_name: "",
      last_name: "",
      email: uLike,
      profilePicture: null,
    };
  }

  return null;
};

const hydrateRepList = (val, userIdx) => {
  const arr = Array.isArray(val) ? val : val != null ? [val] : [];
  const mapped = arr
    .map((x) => asUserShape(x, userIdx))
    .filter(Boolean)
    // de-dup by email/id
    .reduce((acc, u) => {
      const key = u.id || u.email || `${u.first_name} ${u.last_name}`;
      if (!acc.map.has(key)) {
        acc.map.set(key, true);
        acc.out.push(u);
      }
      return acc;
    }, { map: new Map(), out: [] }).out;
  return mapped;
};

const normalizeRole = (role) => String(role || "").trim().replace(/[\s_]+/g, "-").toLowerCase();
const SELECTABLE_REP_ROLES = new Set(["order-rep", "client-rep", "administrator"]);
const isSelectableRepRole = (role) => SELECTABLE_REP_ROLES.has(String(role || "").trim().toLowerCase());
const commissionEligibleRole = (role) => {
  const r = normalizeRole(role);
  return r === "order-rep" || r === "client-rep" || ["administrator", "super-administrator"].includes(r) || r === "admin";
};

const pickRepByRole = (reps = [], primaryRole = "", fallbackRole = "administrator") => {
  const list = Array.isArray(reps) ? reps : [];
  if (!list.length) return [];
  const primary = normalizeRole(primaryRole);
  const fallback = normalizeRole(fallbackRole);
  const exact = list.find((u) => normalizeRole(u?.role) === primary);
  if (exact) return [exact];
  const alt = list.find((u) => normalizeRole(u?.role) === fallback);
  if (alt) return [alt];
  return [list[0]];
};

// ─────────────────────────────────────────────
// Avatars + names (match Purchasing look/feel)
// ─────────────────────────────────────────────
function RepList({ reps, emptyLabel = "—" }) {
  const list = Array.isArray(reps) ? reps : [];
  if (!list.length) return <span>{emptyLabel}</span>;

  const displayName = (u) => {
    const fn = (u?.first_name || "").trim();
    const ln = (u?.last_name || "").trim();
    const n = [fn, ln].filter(Boolean).join(" ");
    return n || u?.email || `User ${u?.id ?? ""}`;
  };

  const initials = (nameOrEmail) =>
    (nameOrEmail || "")
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const allNames = list.map(displayName).join(", ");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }} title={allNames}>
      {list.slice(0, 3).map((u, i) => {
        const name = displayName(u);
        return (
          <Avatar
            key={(u.id || u.email || name) + String(i)}
            src={u.profilePicture || undefined}
            alt={name}
            sx={{ width: 26, height: 26, fontSize: 12 }}
          >
            {initials(name)}
          </Avatar>
        );
      })}
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {allNames}
      </div>
      {list.length > 3 && <Chip size="small" variant="outlined" label={`+${list.length - 3}`} />}
    </Box>
  );
}

// role helper
const isFinance = (role) => {
  const r = String(role || "").toLowerCase();
  return r === "finance-rep" || ["administrator", "super-administrator"].includes(r) || r === "admin" || r === "finance";
};

const getViewerRole = (role) => normalizeRole(role);

const READINESS_REASON_LABELS = {
  INVOICE_NOT_PAID: "Invoice has not been paid",
  INVOICE_NOT_FINALIZED: "No finalized invoice found",
  MARGIN_NOT_FINAL: "Margin inputs are incomplete",
  RATE_NOT_CONFIGURED: "No commission plan is assigned",
};

const READINESS_REASON_NEXT_STEP = {
  INVOICE_NOT_PAID: "Mark the invoice as paid and ensure invoice paid date is set.",
  INVOICE_NOT_FINALIZED: "Create/finalize at least one invoice for this order.",
  MARGIN_NOT_FINAL: "Enter/finalize supplier costs so billed margin can be calculated.",
  RATE_NOT_CONFIGURED: "Assign a commission plan/rate to the rep for this instance.",
};

const getPaymentChipMeta = (row) => {
  const status = String(row?.paymentStatus || "unpaid").toLowerCase();
  if (status === "paid") return { label: "Paid", color: "success", variant: "filled" };
  if (status === "partial") return { label: "Partially Paid", color: "info", variant: "filled" };
  if (status === "exported") return { label: "Exported", color: "primary", variant: "filled" };
  if (status === "reversed") return { label: "Reversed", color: "warning", variant: "outlined" };
  if (status === "locked") return { label: "Locked", color: "default", variant: "filled" };
  if (status === "failed" || status === "error") return { label: "Error", color: "error", variant: "filled" };
  return { label: "Unpaid", color: "warning", variant: "filled" };
};

const toMoneyNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Number(n.toFixed(2));
};
const getActualBilledCosts = (row) => toMoneyNumber(row?.billedCosts ?? row?.billedActual ?? 0);
const getEstimatedCost = (row) => toMoneyNumber(row?.estimatedCost ?? row?.cartEstimatedCost ?? row?.snapshot?.cartEstimatedCost ?? 0);
const getInvoiceSubtotalExTax = (row) => toMoneyNumber(row?.invoiceSubTotal ?? row?.cartSubtotal ?? row?.snapshot?.subtotal ?? 0);
const getCommissionRate = (row) => {
  const raw = Number(row?.activeRate ?? row?.rate ?? row?.snapshot?.rate ?? 0);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return raw > 1 ? raw / 100 : raw;
};
const getMarginMultiplier = (row = {}) => {
  const snap = row?.snapshot && typeof row.snapshot === "object" ? row.snapshot : {};
  const calcTotals = snap?.calculatorTotals && typeof snap.calculatorTotals === "object"
    ? snap.calculatorTotals
    : {};

  const rawCandidates = [
    row?.marginMultiplier,
    row?.calculatorSnapshotMeta?.marginMultiplier,
    row?.systemCalculationSnapshot?.marginMultiplier,
    calcTotals?.marginMultiplier,
    row?.marginRuleRawMultiplier != null ? Number(row.marginRuleRawMultiplier) / 100 : null,
    row?.calculatorSnapshotMeta?.marginRuleRawMultiplier != null ? Number(row.calculatorSnapshotMeta.marginRuleRawMultiplier) / 100 : null,
    calcTotals?.marginRuleRawMultiplier != null ? Number(calcTotals.marginRuleRawMultiplier) / 100 : null,
  ];

  for (const value of rawCandidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }

  return 1;
};
const getLiveCommission = (row) => {
  const final = Number(resolveFinalCalculatedPayout(row) || 0);
  if (final > 0) return toMoneyNumber(final);

  const base = Math.max(0, getInvoiceSubtotalExTax(row) - getActualBilledCosts(row));
  const rate = getCommissionRate(row);
  const multiplier = getMarginMultiplier(row);
  return toMoneyNumber(base * rate * multiplier);
};
const getEstimatedFullCommission = (row) => toMoneyNumber(Math.max(0, getInvoiceSubtotalExTax(row) - getEstimatedCost(row)) * getCommissionRate(row));
const getCostCompletionRatio = (row) => {
  const est = getEstimatedCost(row);
  if (!(est > 0)) return 0;
  return Math.min(1, getActualBilledCosts(row) / est);
};
const firstPositiveMoney = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && Math.abs(n) > 0.009) return n;
  }
  return 0;
};
const resolveFinalCalculatedPayout = (row = {}) => {
  const snap = (row?.snapshot && typeof row.snapshot === "object") ? row.snapshot : {};
  const rec = (snap?.recipient && typeof snap.recipient === "object") ? snap.recipient : {};
  const calcTotals = (snap?.calculatorTotals && typeof snap.calculatorTotals === "object") ? snap.calculatorTotals : {};
  const isCalculatorRow =
    row?.sourceType === "commission_calculator" ||
    row?.payoutAmountSource === "snapshot_or_event" ||
    Object.keys(calcTotals).length > 0 ||
    Object.keys(rec).length > 0;

  if (isCalculatorRow) {
    return firstPositiveMoney(
      snap?.expectedCommissionAmount,
      rec?.commissionAmount,
      row?.originalSnapshotCommissionAmount,
      row?.payoutAmount,
      row?.commissionAmount,
      row?.totalCommissionAmount,
      row?.eventAmount,
      row?.amount
    );
  }

  return firstPositiveMoney(
    row?.systemCalculatedCommissionAmount,
    row?.originalSnapshotCommissionAmount,
    row?.expectedCommissionAmount,
    snap?.expectedCommissionAmount,
    rec?.commissionAmount,
    row?.payoutAmountSource === "calculated" ? row?.payoutAmount : null,
    row?.commissionAmount,
    row?.totalCommissionAmount,
    row?.eventAmount,
    row?.amount
  );
};
const isIncompleteCostRow = (row) => ["partial_costs", "no_costs"].includes(String(row?.costStatus || "")) || (Number(row?.expectedPoCount || 0) > 0 && Number(row?.billedPoCount || 0) < Number(row?.expectedPoCount || 0));
const getSuggestedPrepaymentAmount = (row) => toMoneyNumber(getEstimatedFullCommission(row) * getCostCompletionRatio(row));
const OVERRIDE_PERCENT_BASIS_OPTIONS = [
  { value: "margin", label: "Margin / profit" },
  { value: "subtotal", label: "Invoice subtotal" },
];
const getPercentBase = (basis, { liveMargin, invoiceSubtotal }) => {
  if (basis === "subtotal") return Number(invoiceSubtotal || 0);
  return Number(liveMargin || 0);
};
const getEquivalentPercent = (systemAmount, base) => {
  const amount = Number(systemAmount || 0);
  const b = Number(base || 0);
  if (!(amount > 0) || !(b > 0)) return 0;
  return (amount / b) * 100;
};
const roundTo = (value, decimals = 2) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * factor) / factor;
};
const formatPercentInput = (value) => {
  const n = roundTo(value, 2);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};
const roundMoney = (value) => roundTo(value, 2);
const normalizeMoneyForDisplay = (value) => {
  const rounded = roundMoney(value);
  return Math.abs(rounded) < 0.01 ? 0 : rounded;
};
const isCommissionRowPaidOrFinalized = (row = {}) => {
  const total = Number(row.totalCommissionAmount ?? row.payoutAmount ?? row.commissionAmount ?? 0);
  const paid = Number(row.amountPaid ?? row.paidToDate ?? 0);
  const balance = Number(row.balanceRemaining ?? Math.max(0, total - paid));
  const status = String(row.paymentStatus || row.commissionStatus || "").toLowerCase();

  return !!row.paidAt
    || !!row.isPosted
    || status === "paid"
    || status === "exported"
    || (total > 0 && paid >= total - 0.01)
    || (total > 0 && balance <= 0 && paid > 0);
};

const hasExistingOverrideForRow = (row = {}) => {
  if (!row) return false;
  const system = Number(row.calculatedCommissionAmount ?? row.originalSnapshotCommissionAmount ?? row.eventAmount ?? 0);
  const payout = Number(row.payoutAmount ?? row.commissionAmount ?? row.amount ?? 0);
  return Boolean(
    row.hasOverride === true
      || row.overrideType === "amount"
      || row.overrideType === "percent"
      || row.overrideAmount != null
      || row.overrideRate != null
      || Math.abs(payout - system) >= 0.01
  );
};

const resolveDisplayPayoutAmount = (row = {}) => {
  const hasOverride = hasExistingOverrideForRow(row);
  if (hasOverride) {
    return Number(
      row.payoutAmount
        ?? row.totalCommissionAmount
        ?? row.commissionAmount
        ?? row.amount
        ?? row.eventAmount
        ?? row.calculatedCommissionAmount
        ?? 0
    );
  }
  return Number(
    row.payoutAmount
      ?? row.commissionAmount
      ?? row.commissionDue
      ?? row.calculatedCommissionAmount
      ?? 0
  );
};

const resolveDisplayOverrideAmount = (row = {}) => {
  if (!hasExistingOverrideForRow(row)) return null;
  return Number(
    row.overrideAmount
      ?? row.commissionAmountOverride
      ?? row.payoutAmount
      ?? row.totalCommissionAmount
      ?? row.commissionAmount
      ?? row.amount
      ?? 0
  );
};

/** ────────────────────────────────────────────────────────────────────────────
 * Small inline dialog to edit a single commission override + notes
 */
function EditCommissionDialog({ open, onClose, onSaved, row, instanceUniqueId, instanceId }) {
  const [saving, setSaving] = useState(false);
  const [overrideMode, setOverrideMode] = useState("calculated"); // calculated | amount | percent
  const [overrideAmount, setOverrideAmount] = useState("");
  const [overridePercent, setOverridePercent] = useState("");
  const [overrideBasis, setOverrideBasis] = useState("margin");
  const [overridePercentExact, setOverridePercentExact] = useState(null);
  const [overridePercentDirty, setOverridePercentDirty] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    const hasOverride = hasExistingOverrideForRow(row);
    const type = row?.overrideType === "percent" || row?.overrideRate != null
      ? "percent"
      : row?.overrideType === "amount" || row?.overrideAmount != null || row?.commissionAmountOverride != null
        ? "amount"
        : null;
    const invoiceSubtotal = Number(row?.invoiceSubTotal ?? 0);
    const billedCosts = Number(row?.billedCosts ?? row?.billedActual ?? 0);
    const liveMargin = Number(row?.billedMargin ?? (invoiceSubtotal - billedCosts));
    const fallbackCalculated = resolveFinalCalculatedPayout(row);
    setOverrideMode(type === "percent" ? "percent" : (hasOverride ? "amount" : "calculated"));
    setOverrideAmount(String(row?.overrideAmount ?? row?.commissionAmountOverride ?? fallbackCalculated));
    if (type === "percent" && row?.overrideRate != null) {
      const storedBasis = String(row?.overrideBasis || "margin");
      const normalizedBasis = storedBasis === "subtotal" ? "subtotal" : "margin";
      setOverrideBasis(normalizedBasis);
      setOverridePercentDirty(false);
      if (storedBasis === "calculated_commission") {
        const fallbackBase = getPercentBase(normalizedBasis, { liveMargin, invoiceSubtotal });
        const exact = getEquivalentPercent(fallbackCalculated, fallbackBase);
        setOverridePercentExact(exact);
        setOverridePercent(formatPercentInput(exact));
      } else {
        setOverridePercentExact(null);
        setOverridePercent(formatPercentInput(Number(row.overrideRate) * 100));
      }
    } else {
      setOverrideBasis("margin");
      const exact = getEquivalentPercent(fallbackCalculated, liveMargin);
      setOverridePercentExact(exact);
      setOverridePercent(formatPercentInput(exact));
      setOverridePercentDirty(false);
    }
    setNotes(row?.overrideNotes || row?.commissionNotes || "");
  }, [open, row]);

  const systemAmount = resolveFinalCalculatedPayout(row);
  const systemRate = Number(row?.calculatedCommissionRate ?? row?.activeRate ?? 0);
  const systemBasis = String(row?.calculatedCommissionBasis || row?.basis || "margin");
  const invoiceSubtotal = Number(row?.invoiceSubTotal ?? 0);
  const billedCosts = Number(row?.billedCosts ?? row?.billedActual ?? 0);
  const estCosts = Number(row?.estimatedCost ?? 0);
  const liveMargin = Number(row?.billedMargin ?? (invoiceSubtotal - billedCosts));
  const liveMarginPct = Number(row?.marginPct ?? (invoiceSubtotal > 0 ? (liveMargin / invoiceSubtotal) * 100 : 0));
  const calcBase = Number(row?.calculatedCommissionBase ?? (systemBasis === "subtotal" ? invoiceSubtotal : liveMargin));
  const marginMultiplier = getMarginMultiplier(row);
  const percentPreviewBase = getPercentBase(overrideBasis, { liveMargin, invoiceSubtotal });
  const isAutoPercentMatch =
    overrideMode === "percent"
    && !overridePercentDirty
    && overridePercentExact != null;
  const effectiveOverridePercent = isAutoPercentMatch
    ? Number(overridePercentExact)
    : Number(overridePercent || 0);
  const percentPreviewAmount = isAutoPercentMatch
    ? roundMoney(systemAmount)
    : roundMoney(percentPreviewBase * (effectiveOverridePercent / 100));
  const payoutPreview = overrideMode === "calculated"
    ? roundMoney(systemAmount)
    : overrideMode === "amount"
      ? roundMoney(toNumber(overrideAmount))
      : percentPreviewAmount;
  const rawDifference = payoutPreview - roundMoney(systemAmount);
  const difference = normalizeMoneyForDisplay(rawDifference);
  const hasExistingOverride = hasExistingOverrideForRow(row);
  const existingOverrideMode =
    row?.overrideType === "percent" || row?.overrideRate != null
      ? "percent"
      : row?.overrideType === "amount" || row?.overrideAmount != null || row?.commissionAmountOverride != null
        ? "amount"
        : "calculated";
  const notesChanged = String(notes || "").trim() !== String(row?.overrideNotes || row?.commissionNotes || "").trim();
  const amountChanged = Math.abs(roundMoney(toNumber(overrideAmount)) - roundMoney(systemAmount)) >= 0.01;
  const modeChanged = overrideMode !== existingOverrideMode;
  const hasMeaningfulOverrideChange =
    (overrideMode === "calculated" && hasExistingOverride)
    || (overrideMode === "amount" && (amountChanged || modeChanged || notesChanged))
    || (overrideMode === "percent" && (overridePercentDirty || modeChanged || notesChanged));
  const saveDisabled = saving || !hasMeaningfulOverrideChange;
  const costStatusText = row?.costStatus === "no_costs"
    ? "No supplier bills entered yet. Commission is based on full invoice subtotal."
    : row?.costStatus === "partial_costs"
      ? "Supplier costs are incomplete. Commission may change as bills are added."
      : row?.costStatus === "complete_or_over"
        ? "Costs are complete or meet/exceed estimate."
        : "Cost estimate is unavailable.";

  const handleSave = async () => {
    try {
      setSaving(true);
      const clearOverride = overrideMode === "calculated";
      if (clearOverride && !hasExistingOverride) {
        onClose?.();
        return;
      }
      if (!hasMeaningfulOverrideChange) {
        onClose?.();
        return;
      }
      const payload = {
        commissionPayoutEventId: row?.commissionPayoutEventId || undefined,
        instanceUniqueId,
        instanceId: instanceId || undefined,
        clearOverride,
        overrideType: clearOverride ? null : overrideMode,
        overrideAmount: clearOverride ? null : (overrideMode === "amount" ? toNumber(overrideAmount) : toNumber(percentPreviewAmount)),
        overrideRate: clearOverride ? null : (overrideMode === "percent" ? (toNumber(overridePercent) / 100) : null),
        overrideBasis: clearOverride ? null : (overrideMode === "percent" ? overrideBasis : null),
        notes: clearOverride ? "" : (notes || "").trim(),
      };
      await updateCommissionOverride(row?.orderId, payload);

      const updated = {
        ...row,
        hasOverride: !clearOverride,
        payoutAmountSource: clearOverride ? "calculated" : "override",
        payoutAmount: clearOverride ? systemAmount : Number(payload.overrideAmount || 0),
        commissionAmount: clearOverride ? systemAmount : Number(payload.overrideAmount || 0),
        totalCommissionAmount: clearOverride ? systemAmount : Number(payload.overrideAmount || 0),
        commissionDue: clearOverride ? systemAmount : Number(payload.overrideAmount || 0),
        commissionAmountOverride: clearOverride ? null : payload.overrideAmount,
        commissionNotes: clearOverride ? null : payload.notes,
        overrideType: payload.overrideType,
        overrideAmount: clearOverride ? null : payload.overrideAmount,
        overrideRate: payload.overrideRate,
        overrideBasis: payload.overrideBasis,
        overrideNotes: clearOverride ? null : payload.notes,
        _overridden: !clearOverride,
      };
      onSaved?.(updated);
      onClose?.();
    } catch (e) {
      console.error("Override save failed", e);
      alert(e?.response?.data?.error || "Failed to save override.");
    } finally {
      setSaving(false);
    }
  };
  const handleModeChange = (nextMode) => {
    setOverrideMode(nextMode);
    if (nextMode === "percent") {
      const defaultBasis = "margin";
      const base = getPercentBase(defaultBasis, { liveMargin, invoiceSubtotal });
      setOverrideBasis(defaultBasis);
      const exact = getEquivalentPercent(systemAmount, base);
      setOverridePercentExact(exact);
      setOverridePercent(formatPercentInput(exact));
      setOverridePercentDirty(false);
    }
  };
  const handlePercentBasisChange = (nextBasis) => {
    setOverrideBasis(nextBasis);
    const base = getPercentBase(nextBasis, { liveMargin, invoiceSubtotal });
    const exact = getEquivalentPercent(systemAmount, base);
    setOverridePercentExact(exact);
    setOverridePercent(formatPercentInput(exact));
    setOverridePercentDirty(false);
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Commission Override</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Typography variant="body2"><b>Rep:</b> {row?.repDisplayName || row?.repUserId || "—"}</Typography>
          <Typography variant="body2"><b>Order #:</b> {row?.orderNumber || row?.orderId || "—"}</Typography>
          <Typography variant="body2"><b>Client:</b> {row?.clientName || "—"}</Typography>
          <Typography variant="body2"><b>Invoice(s):</b> {(row?.invoiceNumbers || [row?.invoiceNumber]).filter(Boolean).join(", ") || "—"}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" color={row?.hasOverride ? "warning" : "success"} label={row?.hasOverride ? "Overridden" : "Calculated"} />
          <Chip size="small" variant="outlined" label={row?.costStatusLabel || "Cost status"} />
          {row?.costStatus === "partial_costs" && <Chip size="small" color="warning" label="Partial costs" />}
        </Box>
        <Typography variant="subtitle2">System calculation</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Typography variant="body2">Invoice subtotal (ex tax): <b>{currency(invoiceSubtotal)}</b></Typography>
          <Typography variant="body2">Billed costs (ex tax): <b>{currency(billedCosts)}</b></Typography>
          <Typography variant="body2">Estimated cost (ex tax): <b>{currency(estCosts)}</b></Typography>
          <Typography variant="body2">Live gross margin: <b>{currency(liveMargin)} ({liveMarginPct.toFixed(2)}%)</b></Typography>
          <Typography variant="body2">Calculation basis: <b>{systemBasis}</b></Typography>
          <Typography variant="body2">Commission rate: <b>{percent(systemRate)}</b></Typography>
          {marginMultiplier > 0 && (
            <Typography variant="body2">Margin multiplier: <b>{Number(marginMultiplier).toFixed(2)}</b></Typography>
          )}
          <Typography variant="body2">Calculation base: <b>{currency(calcBase)}</b></Typography>
          <Typography variant="body2">System-calculated commission: <b>{currency(systemAmount)}</b></Typography>
        </Box>
        <Alert severity={row?.costStatus === "complete_or_over" ? "success" : (row?.costStatus === "unknown_estimate" ? "info" : "warning")}>{costStatusText}</Alert>
        <Typography variant="subtitle2">Override controls</Typography>
        <TextField select label="Mode" value={overrideMode} onChange={(e) => handleModeChange(e.target.value)} fullWidth>
          <MenuItem value="calculated">Use calculated commission</MenuItem>
          <MenuItem value="amount">Override by fixed amount</MenuItem>
          <MenuItem value="percent">Override by percentage</MenuItem>
        </TextField>
        {overrideMode === "amount" && <TextField label="Override amount (CA$)" type="number" value={overrideAmount} onChange={(e) => setOverrideAmount(e.target.value)} fullWidth />}
        {overrideMode === "percent" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
            <TextField label="Override percent (%)" type="number" value={overridePercent} onChange={(e) => {
              setOverridePercent(e.target.value);
              setOverridePercentExact(null);
              setOverridePercentDirty(true);
            }} fullWidth />
            {isAutoPercentMatch && (
              <Typography variant="caption" color="text.secondary">
                Auto-set to match the calculated commission. Edit the percent to override.
              </Typography>
            )}
            <TextField
              select
              label="Apply percentage to"
              value={overrideBasis}
              onChange={(e) => handlePercentBasisChange(e.target.value)}
              fullWidth
            >
              {OVERRIDE_PERCENT_BASIS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              {overrideBasis === "margin"
                ? "Percent is applied to gross profit: subtotal minus costs."
                : overrideBasis === "subtotal"
                  ? "Percent is applied to invoice subtotal before tax."
                  : "Percent is applied to the final system-calculated commission."}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
          <Typography variant="body2">System calculated: <b>{currency(systemAmount)}</b></Typography>
          <Typography variant="body2">Override payout: <b>{currency(payoutPreview)}</b></Typography>
          <Typography variant="body2">Difference: <b style={{ color: difference >= 0 ? "#1b5e20" : "#b71c1c" }}>{currency(difference)}</b></Typography>
        </Box>
        <TextField
          label="Override notes/reason"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          minRows={3}
          multiline
          placeholder="Why is this override needed?"
        />
        <Alert severity="info">This does not change invoices, bills, tax, or accounting records. It only controls the commission payout amount.</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        {!hasMeaningfulOverrideChange && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            No override changes to save.
          </Typography>
        )}
        <Button onClick={handleSave} variant="contained" disabled={saveDisabled}>
          {saving
            ? "Saving…"
            : (overrideMode === "calculated" && hasExistingOverride
              ? "Clear Override"
              : "Save Override")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BulkCommissionActionDialog({
  open,
  mode, // pay | pay_export_xlsx | unpay
  onClose,
  onConfirm,
  selectedRows = [],
  selectedPaymentIds = [],
  selectedBatchIds = [],
  defaultDate,
  processing = false,
}) {
  const [paymentDate, setPaymentDate] = useState(defaultDate || new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [ackIncomplete, setAckIncomplete] = useState(false);
  const [manualPayNowAmount, setManualPayNowAmount] = useState("");
  useEffect(() => {
    if (!open) return;
    setPaymentDate(defaultDate || new Date().toISOString().slice(0, 10));
    setReference("");
    setNotes("");
    setReason("");
    setAckIncomplete(false);
    setManualPayNowAmount("");
  }, [open, defaultDate]);
  const payRows = selectedRows.filter((r) => !!r.commissionPayoutEventId && !!r.readyToPay);
  const payNowTotal = payRows.reduce((n, r) => {
    const total = Number(r.totalCommissionAmount ?? r.commissionDue ?? r.payoutAmount ?? r.commissionAmount ?? 0);
    const paid = Number(r.amountPaid ?? r.paidToDate ?? 0);
    const bal = Math.max(0, Number(r.balanceRemaining ?? (total - paid)));
    return n + bal;
  }, 0);
  const repCount = new Set(selectedRows.map((r) => String(r.repUserId || "")).filter(Boolean)).size;
  const paidRows = selectedRows.filter((r) => !!r.paidAt || !!r.isPosted || Number(r.paidToDate || 0) > 0);
  const paidAmount = paidRows.reduce((n, r) => n + Number(r.amountPaid ?? r.paidToDate ?? r.payoutAmount ?? 0), 0);
  const refs = Array.from(new Set(paidRows.map((r) => r.paymentReference || r.paymentBatchId).filter(Boolean)));
  const notReady = selectedRows.filter((r) => !r.readyToPay);
  const missingEvent = selectedRows.filter((r) => !r.commissionPayoutEventId);
  const partialCostRows = selectedRows.filter((r) => isIncompleteCostRow(r));
  const suggestedPayNowTotal = partialCostRows.reduce((n, r) => n + getSuggestedPrepaymentAmount(r), 0);
  const title = mode === "pay" ? "Pay selected commissions" : mode === "pay_export_xlsx" ? "Pay and export selected commissions" : "Un-pay selected commissions";
  const effectivePayNow = mode !== "unpay" && manualPayNowAmount !== "" ? Number(manualPayNowAmount || 0) : payNowTotal;
  const confirmLabel = mode === "pay" ? `Mark ${currency(effectivePayNow)} Paid` : mode === "pay_export_xlsx" ? `Mark ${currency(effectivePayNow)} Paid + Export XLSX` : "Un-pay Commissions";
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 1.25 }}>
        <Typography variant="body2"><b>Pay now based on live actuals:</b> {currency(payNowTotal)} • <b>Rows:</b> {selectedRows.length} • <b>Reps:</b> {repCount}</Typography>
        {mode === "pay_export_xlsx" && <Typography variant="body2">File format: <b>XLSX</b></Typography>}
        {mode !== "unpay" && <Typography variant="body2">You are about to mark the following commission balance(s) as paid.</Typography>}
        {(notReady.length > 0 || missingEvent.length > 0 || (mode === "unpay" && paidRows.length === 0)) && (
          <Alert severity="info">
            {notReady.length > 0 && `Not ready rows: ${notReady.length}. `}
            {missingEvent.length > 0 && `Missing payout id rows: ${missingEvent.length}. `}
            {mode === "unpay" && paidRows.length === 0 && "No paid rows in current selection. "}
          </Alert>
        )}
        {mode !== "unpay" ? (
          <>
            <Typography variant="caption" color="text.secondary" display="block">Payable payment count: {payRows.length}</Typography>
            {selectedRows.some((r) => Number(r.paidToDate || 0) > 0) && (
              <Alert severity="info">
                {selectedRows.filter((r) => Number(r.paidToDate || 0) > 0).map((r) => (
                  <Typography key={`paid-${r.commissionPayoutEventId || r.orderId}`} variant="caption" display="block">
                    Already paid/prepaid: {currency(r.paidToDate || 0)} · Last payment: {(r.lastPaidAt ? new Date(r.lastPaidAt).toLocaleDateString() : "—")} · {r.lastPaymentReference || "No ref"}
                  </Typography>
                ))}
              </Alert>
            )}
            {selectedRows.slice(0, 5).map((r) => {
              const paid = Number(r.amountPaid ?? r.paidToDate ?? 0);
              const due = Number(r.totalCommissionAmount ?? r.commissionDue ?? r.payoutAmount ?? 0);
              const bal = Math.max(0, Number(r.balanceRemaining ?? (due - paid)));
              const revenueBasis = Number(r.invoiceSubTotal ?? 0);
              const billedCosts = Number(r.totalLiveBilledCostsExTax ?? r.billedCosts ?? 0);
              const liveMargin = revenueBasis - billedCosts;
              const rate = Number(r.calculatedCommissionRate ?? r.activeRate ?? 0);
              const marginMultiplier = getMarginMultiplier(r);
              const liveCalc = getLiveCommission(r);
              const delta = normalizeMoneyForDisplay(due - liveCalc);
              const hasOverride = hasExistingOverrideForRow(r);
              const shouldShowStoredDelta =
                Math.abs(delta) > 0.01 &&
                !(
                  String(r.sourceType || "") === "commission_calculator" &&
                  Math.abs(Number(resolveFinalCalculatedPayout(r) || 0) - Number(due || 0)) < 0.01
                );
              return <Box key={`payrow-${r.commissionPayoutEventId || r.orderId}`} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                <Typography variant="caption" display="block"><b>{r.repDisplayName || `Rep #${r.repUserId || "—"}`} · Order {r.orderNumber || r.OrderId || r.orderId || "—"} · {r.invoiceNumber || `INV-${r.invoiceId || "—"}`}</b></Typography>
                <Typography variant="caption" display="block">Pay now: {currency(bal)}</Typography>
                <Typography variant="caption" display="block">Commission total: {currency(due)} · Already paid: {currency(paid)} · Remaining after payment: {currency(Math.max(0, due - paid - bal))}</Typography>
                {paid > 0 && <Typography variant="caption" display="block">This is a remaining-balance payment, not the full original commission.</Typography>}
                <Typography variant="caption" display="block">Revenue basis: {currency(revenueBasis)} ex tax · Current billed costs used: {currency(billedCosts)} ex tax · Current live margin basis: {currency(liveMargin)}</Typography>
                <Typography variant="caption" display="block">Commission rate: {(rate * 100).toFixed(2)}% · Margin multiplier: {Number(marginMultiplier).toFixed(2)} · Live commission for context: {currency(liveCalc)}</Typography>
                {isIncompleteCostRow(r) && <Typography variant="caption" display="block">Estimated cost reference: {currency(getEstimatedCost(r))} ex tax · Estimated remaining reference: {currency(Math.max(0, getEstimatedCost(r) - getActualBilledCosts(r)))} ex tax · Estimate is reference only and is not used to calculate payout.</Typography>}
                {shouldShowStoredDelta && hasOverride && (
                  <>
                    <Typography variant="caption" display="block">Override applied.</Typography>
                    <Typography variant="caption" display="block">System calculated {currency(liveCalc)} · Override payout {currency(due)} · Difference {currency(delta)}</Typography>
                  </>
                )}
                {shouldShowStoredDelta && !hasOverride && (
                  <Typography variant="caption" display="block">Stored payout {currency(due)} · Current calculated {currency(liveCalc)} · Difference {currency(delta)}</Typography>
                )}
              </Box>;
            })}
            <Alert severity="warning">
              <Typography variant="body2"><b>Review before paying</b></Typography>
              <Typography variant="caption" display="block">This action marks selected commissions as paid and affects payout reporting.</Typography>
              {!!partialCostRows.length && <>
                <Typography variant="caption" display="block">Supplier costs are incomplete. This payment is based on actual bills entered so far. Future bills may change the final margin.</Typography>
                {partialCostRows.slice(0, 3).map((r) => (
                  <Typography key={`pc-${r.commissionPayoutEventId || r.orderId}`} variant="caption" display="block">
                    Order {r.orderNumber || r.OrderId || r.orderId}: {r.billedPoCount || 0}/{r.expectedPoCount || 0} bills entered · billed {currency(r.totalLiveBilledCostsExTax ?? r.billedCosts ?? 0)} ex tax · estimated {currency(r.totalEstimatedCostExTax ?? r.estimatedCost ?? 0)} ex tax · remaining {currency(r.estimatedRemainingCostExTax ?? r.estimatedRemainingCost ?? 0)} ex tax
                  </Typography>
                ))}
                <Typography variant="caption" display="block">This commission is being paid using the current live billed costs entered so far, not the final estimated project cost.</Typography>
                <Typography variant="caption" display="block">Future supplier bills may change the final margin, but this action will mark the current commission balance as paid.</Typography>
              </>}
              <Typography variant="caption" display="block">This payment is based on the current live commission balance.</Typography>
            </Alert>
            {!!partialCostRows.length && <>
              <Typography variant="caption" color="text.secondary">Estimates are shown for context only. Payouts are based on actual invoices and actual bills. Finance may optionally edit pay-now amount as a manual override.</Typography>
              <TextField label={`Optional manual override amount (default live actuals ${currency(payNowTotal)})`} value={manualPayNowAmount} onChange={(e) => setManualPayNowAmount(e.target.value)} />
              <FormControlLabel control={<Checkbox checked={ackIncomplete} onChange={(e) => setAckIncomplete(e.target.checked)} />} label="I understand supplier costs are incomplete and future bills may change final commission." />
            </>}
            <TextField label="Payment Date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Payment Reference / Batch ID (optional)" value={reference} onChange={(e) => setReference(e.target.value)} />
            <TextField label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
          </>
        ) : (
          <>
            <Typography variant="body2"><b>Paid rows selected:</b> {paidRows.length} • <b>Paid amount:</b> {currency(paidAmount)}</Typography>
            <Typography variant="caption" color="text.secondary">References/batches affected: {refs.join(", ") || "—"}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">Reversal mode: Full reversal</Typography>
            <Typography variant="caption" color="text.secondary">Payment IDs: {selectedPaymentIds.join(", ") || "—"} | Batch IDs: {selectedBatchIds.join(", ") || "—"}</Typography>
            <TextField label="Reversal reason" value={reason} onChange={(e) => setReason(e.target.value)} required multiline minRows={2} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={processing}>Cancel</Button>
        <Button variant="contained" color={mode === "unpay" ? "warning" : "primary"} onClick={() => onConfirm({ paymentDate, reference, notes, reason, manualPayNowAmount, ackIncomplete })} disabled={processing || (mode === "unpay" && !reason.trim()) || (mode !== "unpay" && partialCostRows.length > 0 && !ackIncomplete)}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InstanceCommissionsReport() {
  const { instanceUniqueId } = useParams();
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const financeUser = isFinance(authState?.role);
  const viewerRole = getViewerRole(authState?.role);
  const currentUserId = String(authState?.id ?? authState?.userId ?? "").trim();
  const isClientRepViewer = viewerRole === "client-rep";
  const isOrderRepViewer = viewerRole === "order-rep";
  const repScopedViewer = !financeUser && (isClientRepViewer || isOrderRepViewer) && !!currentUserId;

  // filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [repId, setRepId] = useState(""); // numeric/string id for a rep
  const [repType, setRepType] = useState("client"); // 'client' | 'order'
  const [paidStatus, setPaidStatus] = useState(""); // "", "paid", "unpaid", "partial"
  const [state, setState] = useState(""); // DRAFT/APPROVED/PAID
  const [invoiceStatus, setInvoiceStatus] = useState('both');
  const [billStatus, setBillStatus] = useState('both');
  const [commissionStatus, setCommissionStatus] = useState('both');
  const [client, setClient] = useState('');
  const [invoiceDatePaidFrom, setInvoiceDatePaidFrom] = useState('');
  const [invoiceDatePaidTo, setInvoiceDatePaidTo] = useState('');
  const [commissionDatePaidFrom, setCommissionDatePaidFrom] = useState('');
  const [commissionDatePaidTo, setCommissionDatePaidTo] = useState('');
  const [quickFind, setQuickFind] = useState('');
  const [readyFilter, setReadyFilter] = useState('all');
  const [hasRunReport, setHasRunReport] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // data
  const [rawRows, setRawRows] = useState([]);
  const [userDirectory, setUserDirectory] = useState([]);
  const userIndex = useMemo(() => buildUserIndex(userDirectory), [userDirectory]);

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [instanceId, setInstanceId] = useState(null);

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [reversePickerOpen, setReversePickerOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [reversePaymentIds, setReversePaymentIds] = useState([]);

  // Load user directory (same endpoint Purchasing uses)
  useEffect(() => {
    let cancelled = false;
    const headers = { accessToken: localStorage.getItem("accessToken") };
    const loadUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstanceUniqueId/${instanceUniqueId}`,
          { headers }
        );
        if (!cancelled) setUserDirectory(res?.data?.listOfUsers || []);
      } catch (e) {
        if (!cancelled) console.warn("Failed to load users for avatars/names", e);
      }
    };
    loadUsers();
    return () => { cancelled = true; };
  }, [instanceUniqueId]);

  // Enrich rows with rep details (copying Purchasing’s approach)
  const rows = useMemo(() => {
    return (rawRows || []).map((r) => {
      const rawAssigned = (() => {
        if (Array.isArray(r?.AssignedTo)) return r.AssignedTo;
        if (Array.isArray(r?.assignedTo)) return r.assignedTo;
        if (typeof r?.AssignedTo === "string") {
          try { return JSON.parse(r.AssignedTo); } catch { return []; }
        }
        if (typeof r?.assignedTo === "string") {
          try { return JSON.parse(r.assignedTo); } catch { return []; }
        }
        return [];
      })();
      const assignedRepDetails = hydrateRepList(rawAssigned, userIndex).filter((u) => commissionEligibleRole(u?.role));
      // Back-end may already provide details; if not, hydrate from ids/emails
      const clientRepDetails =
        r.clientRepDetails && r.clientRepDetails.length
          ? hydrateRepList(r.clientRepDetails, userIndex)
          : hydrateRepList(
              r.clientRepIds ||
                r.clientReps ||
                r.clientRep ||
                r.clientRepId ||
                r.clientRepEmails ||
                [], userIndex
            );

      const orderRepDetails =
        r.orderRepDetails && r.orderRepDetails.length
          ? hydrateRepList(r.orderRepDetails, userIndex)
          : hydrateRepList(
              r.orderRepIds ||
                r.orderReps ||
                r.orderRep ||
                r.orderRepId ||
                r.orderRepEmails ||
                [], userIndex
            );

      // Provide a clean label for Order # (external), but keep internal id for link
      const orderNumber = r.orderNumber || r.orderNo || r.soNumber || r.OrderId || r.orderId || "";

      // Allow multiple invoice numbers tooltip if provided
      const invoiceNumbers = Array.isArray(r.invoiceNumbers) ? r.invoiceNumbers : r.invoiceNumber ? [r.invoiceNumber] : [];

      return {
        ...r,
        orderNumber,
        clientRepDetails: pickRepByRole(clientRepDetails, "client-rep", "administrator"),
        orderRepDetails: pickRepByRole(orderRepDetails, "order-rep", "administrator"),
        assignedRepDetails,
        invoiceNumbers,
      };
    });
  }, [rawRows, userIndex]);

  const rowsForViewer = useMemo(() => {
    if (!repScopedViewer) return rows;
    return rows.filter((r) => {
      const ids = [
        ...(isOrderRepViewer ? (r.orderRepIds || []) : (r.clientRepIds || [])),
        r.repUserId,
      ].filter((id) => id != null);
      return ids.map(String).includes(String(currentUserId));
    });
  }, [rows, repScopedViewer, isOrderRepViewer, currentUserId]);


  const visibleRows = useMemo(() => {
    const normalize = (v) => String(v || "").trim().toLowerCase();
    const sameDayOrAfter = (value, min) => {
      if (!min) return true;
      if (!value) return false;
      return new Date(value) >= new Date(`${min}T00:00:00`);
    };
    const sameDayOrBefore = (value, max) => {
      if (!max) return true;
      if (!value) return false;
      return new Date(value) <= new Date(`${max}T23:59:59`);
    };
    const rowMatchesRep = (row) => {
      if (!repId) return true;
      const ids = repType === "order"
        ? [...(row.orderRepIds || []), row.repUserId]
        : [...(row.clientRepIds || []), row.repUserId];
      return ids.map(String).includes(String(repId));
    };
    const rowMatchesInvoiceStatus = (row) => {
      if (invoiceStatus === "both") return true;
      const invoicePaidAmount = Number(row.invoicePaid || row.paidInvoiceTotal || 0);
      const invoiceTotal = Number(row.invoiceTotal || row.invoicedClient || 0);
      const invoiceStatusText = normalize(row.invoiceStatus || row.paymentStatus);
      const invoiceFullyPaid =
        invoiceStatusText === "paid" ||
        invoiceStatusText === "completed" ||
        invoiceStatusText === "complete" ||
        invoiceStatusText.includes("paid") ||
        (invoiceTotal > 0 && invoicePaidAmount >= invoiceTotal - 0.01);
      return invoiceStatus === "paid" ? invoiceFullyPaid : !invoiceFullyPaid;
    };
    const rowMatchesCommissionStatus = (row) => {
      if (commissionStatus === "both") return true;
      const commissionTotal = Number(row.totalCommissionAmount || row.payoutAmount || row.commissionAmount || row.commissionDue || 0);
      const commissionPaidAmount = Number(row.paidToDate || row.amountPaid || 0);
      const commissionStatusText = normalize(row.paymentStatus || row.commissionStatus);
      const commissionFullyPaid =
        !!row.isPosted ||
        !!row.paidAt ||
        commissionStatusText === "paid" ||
        commissionStatusText === "exported" ||
        (commissionTotal > 0 && commissionPaidAmount >= commissionTotal - 0.01);
      return commissionStatus === "paid" ? commissionFullyPaid : !commissionFullyPaid;
    };
    const rowMatchesBillStatus = (row) => {
      if (billStatus === "both") return true;
      const complete = normalize(row.costStatus) === "complete_or_over"
        || (Number(row.expectedPoCount || 0) > 0 && Number(row.billedPoCount || 0) >= Number(row.expectedPoCount || 0));
      return billStatus === "paid" ? complete : !complete;
    };
    const rowMatchesClient = (row) => {
      if (!client) return true;
      const needle = normalize(client);
      const hay = normalize([
        row.businessName,
        row.clientName,
        row.customerName,
        row.ClientName,
        row?.customer?.businessName,
      ].filter(Boolean).join(" "));
      return hay.includes(needle);
    };
    const needle = String(quickFind || '').trim().toLowerCase();
    let filtered = rowsForViewer;
    filtered = filtered.filter((r) => rowMatchesRep(r));
    filtered = filtered.filter((r) => rowMatchesInvoiceStatus(r));
    filtered = filtered.filter((r) => rowMatchesCommissionStatus(r));
    filtered = filtered.filter((r) => rowMatchesBillStatus(r));
    filtered = filtered.filter((r) => rowMatchesClient(r));
    if (state && state !== "all") filtered = filtered.filter((r) => normalize(r.lockState || r.commissionState) === normalize(state));
    if (paidStatus && paidStatus !== "both") {
      filtered = filtered.filter((r) => {
        const isPaid = !!r.paidAt || !!r.isPosted || Number(r.paidToDate || r.amountPaid || 0) > 0;
        return paidStatus === "paid" ? isPaid : !isPaid;
      });
    }
    filtered = filtered.filter((r) => {
      const eventOrInvoiceDate = r.invoiceDate || r.eventDate || null;
      const invoicePaidDate = r.invoiceDatePaid || null;
      const commissionPaidDate = r.commissionDatePaid || r.paidAt || r.lastPaidAt || null;
      return sameDayOrAfter(eventOrInvoiceDate, dateFrom)
        && sameDayOrBefore(eventOrInvoiceDate, dateTo)
        && sameDayOrAfter(invoicePaidDate, invoiceDatePaidFrom)
        && sameDayOrBefore(invoicePaidDate, invoiceDatePaidTo)
        && sameDayOrAfter(commissionPaidDate, commissionDatePaidFrom)
        && sameDayOrBefore(commissionPaidDate, commissionDatePaidTo);
    });
    if (readyFilter === 'ready') filtered = filtered.filter((r) => !!r.readyToPay);
    if (readyFilter === 'not_ready') filtered = filtered.filter((r) => !r.readyToPay);
    if (!needle) return filtered;
    return filtered.filter((r) => {
      const haystack = [
        r.projectTitle,
        r.orderTitle,
        r.clientName,
        r.customerName,
        r.orderNumber,
        r.OrderId,
        r.poNumber,
        r.purchaseOrderNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rowsForViewer, quickFind, readyFilter, repId, repType, paidStatus, state, invoiceStatus, billStatus, commissionStatus, client, dateFrom, dateTo, invoiceDatePaidFrom, invoiceDatePaidTo, commissionDatePaidFrom, commissionDatePaidTo]);

  const repOptions = useMemo(() => {
    const users = Array.isArray(userDirectory)
      ? userDirectory.filter((u) => isSelectableRepRole(u?.role))
      : [];
    const active = users.filter((u) => u?.isActive !== false && String(u?.status || '').toLowerCase() !== 'inactive');
    const inactive = users.filter((u) => !active.includes(u));
    const mk = (u) => ({ id: String(u.id), label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User #${u.id}`, inactive: !active.includes(u) });
    return { active: active.map(mk), inactive: inactive.map(mk) };
  }, [userDirectory]);

  useEffect(() => {
    if (!repId || repScopedViewer || !Array.isArray(userDirectory) || userDirectory.length === 0) return;
    const allowedIds = new Set([...(repOptions.active || []), ...(repOptions.inactive || [])].map((u) => String(u.id)));
    if (!allowedIds.has(String(repId))) {
      setRepId("");
    }
  }, [repId, repOptions, repScopedViewer, userDirectory]);


  const getRowKey = (row) => {
    const rep = row?.repUserId != null ? String(row.repUserId) : 'default';
    const role = String(row?.repRoleType || row?.repType || 'na');
    if (row?.commissionPayoutEventId) return `ev:${String(row.commissionPayoutEventId)}`;
    if (row?.rowKey) return String(row.rowKey);
    return `fallback:${String(row?.orderId || '0')}:${String(row?.invoiceId || '0')}:${rep}:${role}`;
  };

  const selectedRows = useMemo(() => {
    const selectedIds = new Set((selected || []).map((id) => String(id)));
    return visibleRows.filter((row) => selectedIds.has(getRowKey(row)));
  }, [selected, visibleRows]);

  const selectedOrderIds = Array.from(new Set(selectedRows.map((row) => Number(row.orderId)).filter((id) => Number.isInteger(id) && id > 0)));
  const selectedEventIds = Array.from(new Set(selectedRows.map((row) => Number(row.commissionPayoutEventId)).filter((id) => Number.isInteger(id) && id > 0)));

  const selectedNotReadyCount = selectedRows.filter((row) => !row.readyToPay).length;
  const selectedReadyCount = selectedRows.filter((row) => !!row.readyToPay).length;
  const selectedPaidCount = selectedRows.filter((row) => !!row.paidAt || !!row.isPosted || Number(row.paidToDate || 0) > 0).length;
  const selectedUnpaidCount = Math.max(0, selectedRows.length - selectedPaidCount);
  const selectedCommissionTotal = selectedRows.reduce((n, row) => n + Number(row.payoutAmount ?? row.commissionAmount ?? row.commissionDue ?? 0), 0);
  const selectedCalculatedTotal = selectedRows.reduce((n, row) => n + Number(row.calculatedCommissionAmount ?? row.payoutAmount ?? row.commissionAmount ?? row.commissionDue ?? 0), 0);
  const selectedOverrideDelta = selectedCommissionTotal - selectedCalculatedTotal;
  const selectedPaymentIds = Array.from(
    new Set(
      selectedRows
        .flatMap((row) => (Array.isArray(row.paymentIds) ? row.paymentIds : []))
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
    )
  );
  const selectedBatchIds = Array.from(
    new Set(
      selectedRows
        .flatMap((row) => (Array.isArray(row.paymentBatchIds) ? row.paymentBatchIds : []))
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    )
  );
  const hasUnpayScope = selectedPaymentIds.length > 0 || selectedBatchIds.length === 1;
  const selectedUnpayableRows = selectedRows.filter((row) => {
    const st = String(row?.paymentStatus || "").toLowerCase();
    const paidLike = st === "paid" || st === "exported" || st === "partial";
    const locked = st === "locked" || !!row?.isLocked;
    return paidLike && !!row?.canUnpayCommission && !locked;
  });
  const selectedLinkedPayments = useMemo(() => {
    const direct = selectedRows
      .filter((r) => String(r?.entryType || '').toLowerCase() === 'payment' && !!r?.commissionPayoutEventId)
      .map((r) => ({
        id: Number(r.commissionPayoutEventId), repName: r.repDisplayName || null, orderNumber: r.orderNumber || r.OrderId || null, invoiceNumber: r.invoiceNumber || null,
        amount: Number(r.amountPaid || r.payoutAmount || r.commissionAmount || 0), paidAt: r.paidAt || null, reference: r.paymentReference || null, note: r.lastPaymentNote || null, payableAmountSource: 'payment_row',
      }));
    const fromParents = selectedRows.flatMap((r) => (Array.isArray(r.linkedPayments) ? r.linkedPayments : []));
    const all = [...direct, ...fromParents];
    const seen = new Set();
    return all.filter((p) => {
      const id = Number(p.id || 0);
      if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [selectedRows]);
  const allowPayWithIncompleteCosts = true;
  const rowIsIncompleteCost = (row) => ["partial_costs", "no_costs"].includes(String(row?.costStatus || ""));
  const rowHardBlockReason = (row) => {
    if (!row?.commissionPayoutEventId) return "MISSING_PAYOUT_ID";
    if (!financeUser) return "PERMISSION_DENIED";
    if (!instanceId && !row?.instanceId) return "MISSING_INSTANCE_ID";
    if (!row?.readyToPay) return "NOT_READY";
    if (row?.voidedAt) return "VOIDED";
    if (row?.paidAt || row?.isPosted) return "ALREADY_PAID";
    if (!allowPayWithIncompleteCosts && rowIsIncompleteCost(row)) return "POLICY_BLOCKS_INCOMPLETE_COSTS";
    return null;
  };
  const selectedBlockedRows = selectedRows.filter((row) => !!rowHardBlockReason(row));
  const selectedPayableRows = selectedRows.filter((row) => !rowHardBlockReason(row));
  const selectedWarningRows = selectedPayableRows.filter((row) => rowIsIncompleteCost(row));
  const selectedPayableEventIds = Array.from(new Set(selectedPayableRows.map((row) => Number(row.commissionPayoutEventId)).filter((id) => Number.isInteger(id) && id > 0)));
  const getPayDisabledReason = () => {
    if (!selected.length) return "No selected rows";
    if (!financeUser) return "Finance permission required";
    if (!selectedRows.length) return "Selected rows are not in current view";
    if (!selectedEventIds.length) return "Selected rows are missing commission payout IDs";
    if (!instanceId) return "Missing instanceId from report response";
    if (!selectedPayableRows.length) return "Selected rows are not financially ready";
    return null;
  };
  const payDisabledReason = getPayDisabledReason();

  const getDefaultCommissionFilters = useCallback(() => ({
    ...DEFAULT_FILTERS,
    repId: repScopedViewer ? currentUserId : DEFAULT_FILTERS.repId,
    repType: repScopedViewer ? (isOrderRepViewer ? "order" : "client") : DEFAULT_FILTERS.repType,
  }), [currentUserId, isOrderRepViewer, repScopedViewer]);

  const buildCurrentFilters = useCallback(() => ({
    dateFrom,
    dateTo,
    repId,
    repType,
    paidStatus,
    state,
    invoiceStatus,
    billStatus,
    commissionStatus,
    client,
    invoiceDatePaidFrom,
    invoiceDatePaidTo,
    commissionDatePaidFrom,
    commissionDatePaidTo,
    quickFind,
    readyFilter,
    showMoreFilters,
  }), [
    billStatus,
    client,
    commissionDatePaidFrom,
    commissionDatePaidTo,
    commissionStatus,
    dateFrom,
    dateTo,
    invoiceDatePaidFrom,
    invoiceDatePaidTo,
    invoiceStatus,
    paidStatus,
    quickFind,
    readyFilter,
    repId,
    repType,
    showMoreFilters,
    state,
  ]);

  const applyFilterState = useCallback((nextFilters = {}) => {
    setDateFrom(nextFilters.dateFrom || "");
    setDateTo(nextFilters.dateTo || "");
    setRepId(nextFilters.repId || "");
    setRepType(nextFilters.repType || "client");
    setPaidStatus(nextFilters.paidStatus || "");
    setState(nextFilters.state || "");
    setInvoiceStatus(nextFilters.invoiceStatus || "both");
    setBillStatus(nextFilters.billStatus || "both");
    setCommissionStatus(nextFilters.commissionStatus || "both");
    setClient(nextFilters.client || "");
    setInvoiceDatePaidFrom(nextFilters.invoiceDatePaidFrom || "");
    setInvoiceDatePaidTo(nextFilters.invoiceDatePaidTo || "");
    setCommissionDatePaidFrom(nextFilters.commissionDatePaidFrom || "");
    setCommissionDatePaidTo(nextFilters.commissionDatePaidTo || "");
    setQuickFind(nextFilters.quickFind || "");
    setReadyFilter(nextFilters.readyFilter || "all");
    setShowMoreFilters(!!nextFilters.showMoreFilters);
  }, []);

  const normalizeDateFilter = (value) =>
    /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim()) ? String(value).trim() : "";

  const buildValidatedCommissionFilters = useCallback((savedState = {}) => {
    const defaults = getDefaultCommissionFilters();
    const savedFilters = savedState?.filters && typeof savedState.filters === "object"
      ? savedState.filters
      : savedState;
    const nextFilters = {
      ...defaults,
      dateFrom: normalizeDateFilter(savedFilters?.dateFrom),
      dateTo: normalizeDateFilter(savedFilters?.dateTo),
      repId: String(savedFilters?.repId || "").trim(),
      repType: ["client", "order"].includes(String(savedFilters?.repType || "")) ? String(savedFilters.repType) : defaults.repType,
      paidStatus: ["", "paid", "unpaid", "partial"].includes(String(savedFilters?.paidStatus || ""))
        ? String(savedFilters?.paidStatus || "")
        : defaults.paidStatus,
      state: ["", "DRAFT", "APPROVED", "PAID"].includes(String(savedFilters?.state || ""))
        ? String(savedFilters?.state || "")
        : defaults.state,
      invoiceStatus: ["both", "paid", "unpaid"].includes(String(savedFilters?.invoiceStatus || ""))
        ? String(savedFilters?.invoiceStatus || "")
        : defaults.invoiceStatus,
      billStatus: ["both", "paid", "unpaid"].includes(String(savedFilters?.billStatus || ""))
        ? String(savedFilters?.billStatus || "")
        : defaults.billStatus,
      commissionStatus: ["both", "paid", "unpaid"].includes(String(savedFilters?.commissionStatus || ""))
        ? String(savedFilters?.commissionStatus || "")
        : defaults.commissionStatus,
      client: String(savedFilters?.client || ""),
      invoiceDatePaidFrom: normalizeDateFilter(savedFilters?.invoiceDatePaidFrom),
      invoiceDatePaidTo: normalizeDateFilter(savedFilters?.invoiceDatePaidTo),
      commissionDatePaidFrom: normalizeDateFilter(savedFilters?.commissionDatePaidFrom),
      commissionDatePaidTo: normalizeDateFilter(savedFilters?.commissionDatePaidTo),
      quickFind: String(savedFilters?.quickFind || ""),
      readyFilter: ["all", "ready", "not_ready"].includes(String(savedFilters?.readyFilter || ""))
        ? String(savedFilters?.readyFilter || "")
        : defaults.readyFilter,
      showMoreFilters: parseSavedBoolean(savedFilters?.showMoreFilters, defaults.showMoreFilters),
    };

    if (repScopedViewer) {
      nextFilters.repId = currentUserId || "";
      nextFilters.repType = isOrderRepViewer ? "order" : "client";
    }

    return nextFilters;
  }, [currentUserId, getDefaultCommissionFilters, isOrderRepViewer, repScopedViewer]);

  useEffect(() => {
    if (!financeUser || !instanceUniqueId) return;
    let active = true;
    (async () => {
      try {
        const res = await fetchCommissionReadinessPolicy(instanceUniqueId);
        if (!active) return;
        const policy = res?.data?.policy || {};
        setReadinessPolicy({
          requireInvoicePaid: policy.requireInvoicePaid !== false,
          requireInvoiceFinalized: policy.requireInvoiceFinalized !== false,
          requireMarginFinalized: policy.requireMarginFinalized !== false,
          requireAssignedPlan: policy.requireAssignedPlan !== false,
        });
      } catch (e) {
        if (!active) return;
        console.warn('Failed to load commission readiness policy', e);
      }
    })();
    return () => { active = false; };
  }, [financeUser, instanceUniqueId]);

  const setPolicyField = (field) => (event) => {
    const checked = !!event?.target?.checked;
    setReadinessPolicy((prev) => ({ ...prev, [field]: checked }));
  };

  const handleResetPolicyDefaults = () => {
    setReadinessPolicy({
      requireInvoicePaid: true,
      requireInvoiceFinalized: true,
      requireMarginFinalized: true,
      requireAssignedPlan: true,
    });
  };

  const handleSavePolicy = async () => {
    try {
      setPolicySaving(true);
      await updateCommissionReadinessPolicy(instanceUniqueId, readinessPolicy);
      setPolicyDialogOpen(false);
      if (hasRunReport) await runFetch();
    } catch (e) {
      console.error('Failed to update commission readiness policy', e);
      alert('Failed to save readiness policy.');
    } finally {
      setPolicySaving(false);
    }
  };

  // summary
  const summary = useMemo(() => {
    const totals = visibleRows.reduce(
      (acc, r) => {
        if (isManualFlatRow(r)) {
          const payout = Number(r.payoutAmount ?? r.commissionAmount ?? r.commissionDue ?? 0);
          const calc = Number(r.calculatedCommissionAmount ?? payout);
          acc.commissionDue += payout;
          acc.calculatedCommission += calc;
          acc.overrideDelta += (payout - calc);
          return acc;
        }
        acc.invoiceTotal += Number(r.invoiceTotal ?? r.invoicedClient ?? 0);
        const paidVal = Number(r.invoicePaid ?? r.paidToDate ?? 0);
        acc.paid += paidVal;
        const unpaidVal =
          Number(r.invoiceUnpaid ?? r.balance ?? 0) ||
          Math.max(0, Number(r.invoiceTotal ?? r.invoicedClient ?? 0) - paidVal);
        acc.unpaid += unpaidVal;

        acc.billedCosts += Number(r.billedCosts ?? r.billedActual ?? 0);
        const inv = Number(r.invoiceTotal ?? r.invoicedClient ?? 0);
        const costs = Number(r.billedCosts ?? r.billedActual ?? 0);
        acc.billedMargin += Number(r.billedMargin ?? inv - costs);
        const payout = Number(r.payoutAmount ?? r.commissionAmount ?? r.commissionDue ?? 0);
        const calc = Number(r.calculatedCommissionAmount ?? payout);
        acc.commissionDue += payout;
        acc.calculatedCommission += calc;
        acc.overrideDelta += (payout - calc);
        return acc;
      },
      { invoiceTotal: 0, paid: 0, unpaid: 0, billedCosts: 0, billedMargin: 0, commissionDue: 0, calculatedCommission: 0, overrideDelta: 0 }
    );
    const avgMargin = visibleRows.length ? totals.billedMargin / visibleRows.length : 0;
    return { ...totals, avgMargin, count: visibleRows.length };
  }, [visibleRows]);

  // ─────────────────────────────────────────────
  // Columns
  // ─────────────────────────────────────────────
  const COLUMN_DEFS = {
    date: {
      label: "Date",
      build: () => ({
        field: "date",
        headerName: "Date",
        width: 130,
        valueGetter: (value, row) => {
          const d = row?.invoiceDate || row?.soDate || row?.projectDate || row?.createdAt || null;
          return d ? new Date(d).toLocaleDateString() : "—";
        },
      }),
    },
    clientRepName: {
      label: "Client Rep",
      build: () => ({
        field: "clientRepName",
        headerName: "Client Rep",
        width: 260,
        sortable: false,
        renderCell: ({ row }) => <RepList reps={row.clientRepDetails} />,
      }),
    },
    orderRepName: {
      label: "Order Rep",
      build: () => ({
        field: "orderRepName",
        headerName: "Order Rep",
        width: 260,
        sortable: false,
        renderCell: ({ row }) => <RepList reps={row.orderRepDetails} />,
      }),
    },
    rep: {
      label: "Rep",
      build: () => ({
        field: "rep",
        headerName: "Rep",
        width: 280,
        sortable: false,
        renderCell: ({ row }) => (
          <RepList
            reps={
              row.assignedRepDetails?.length
                ? row.assignedRepDetails
                : (repType === "order" ? row.orderRepDetails : row.clientRepDetails)?.length
                  ? (repType === "order" ? row.orderRepDetails : row.clientRepDetails)
                  : row.repDisplayName
                    ? [{ id: row.repUserId, first_name: row.repDisplayName, last_name: "", email: "" }]
                    : []
            }
            emptyLabel={row?.recipientResolution === "default-null" ? "Unassigned" : "—"}
          />
        ),
      }),
    },
    client: {
      label: "Client",
      build: () => ({
        field: "client",
        headerName: "Client",
        width: 220,
        valueGetter: (value, row) =>
          row?.businessName ||
          row?.clientName ||
          row?.customerName ||
          row?.ClientName ||
          row?.customer?.businessName ||
          "—",
      }),
    },
    projectTitle: {
      label: "Project",
      build: () => ({
        field: "projectTitle",
        headerName: "Project",
        width: 260,
        valueGetter: (value, row) => row?.projectTitle || row?.orderTitle || "—",
      }),
    },
    invoiceNumber: {
      label: "Invoices",
      build: () => ({
        field: "invoiceNumber",
        headerName: "Invoices",
        width: 280,
        renderCell: ({ row }) => {
          const list = Array.isArray(row.invoiceNumbers) && row.invoiceNumbers.length
            ? row.invoiceNumbers
            : (row.invoiceNumber ? [row.invoiceNumber] : []);
          if (!list.length) return <span>—</span>;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {list.map((inv, idx) => {
                const invoiceId = row?.invoiceId && list.length === 1 ? row.invoiceId : String(inv || "").split("-").pop();
                const href = `/generated-invoice/${invoiceId}`;
                return (
                  <React.Fragment key={`${inv}-${idx}`}>
                    <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                      {inv}
                    </a>
                    {idx < list.length - 1 ? <span>,</span> : null}
                  </React.Fragment>
                );
              })}
            </Box>
          );
        },
      }),
    },
    orderId: {
      label: "Order #",
      build: () => ({
        field: "orderId",
        headerName: "Order #",
        width: 150,
        renderCell: ({ row }) => {
          const routeOrderId = getSalesOrderRouteId(row);
          const url = routeOrderId ? `/instance/${instanceUniqueId}/sales/orders/${routeOrderId}` : null;
          const label = row.orderNumber || row.OrderId || row.orderId || "—"; // prefer external SO number if available
          return url ? (
            <a href={url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              {label}
            </a>
          ) : (
            <span>{label}</span>
          );
        },
      }),
    },
    invoiceSubTotal: {
      label: "Subtotal",
      build: () => ({
        field: "invoiceSubTotal",
        headerName: "Subtotal",
        width: 130,
        valueGetter: (value, row) => {
          if (isManualFlatRow(row)) return row?.invoiceId ? currency(row?.invoiceSubTotal ?? 0) : "—";
          return currency(row?.invoiceSubTotal ?? row?.actualRevenueNoTax ?? row?.soRevenueSigned ?? row?.subTotal ?? 0);
        },
      }),
    },
    invoiceTax: {
      label: "Taxes",
      build: () => ({
        field: "invoiceTax",
        headerName: "Taxes",
        width: 110,
        valueGetter: (value, row) => (isManualFlatRow(row) ? "—" : currency(row?.invoiceTax ?? row?.taxTotal ?? 0)),
      }),
    },
    invoiceTotal: {
      label: "Total",
      build: () => ({
        field: "invoiceTotal",
        headerName: "Total",
        width: 130,
        valueGetter: (value, row) => (isManualFlatRow(row) ? "—" : currency(row?.invoiceTotal ?? row?.invoicedClient ?? 0)),
      }),
    },
    invoicePaid: {
      label: "Paid",
      build: () => ({
        field: "invoicePaid",
        headerName: "Paid",
        width: 130,
        valueGetter: (value, row) => (isManualFlatRow(row) ? "—" : currency(row?.invoicePaid ?? row?.paidToDate ?? 0)),
      }),
    },
    invoiceDatePaid: {
      label: "Invoice Date Paid",
      build: () => ({
        field: "invoiceDatePaid",
        headerName: "Invoice Date Paid",
        width: 150,
        valueGetter: (value, row) => {
          const d = row?.invoiceDatePaid || null;
          return d ? new Date(d).toLocaleDateString() : "—";
        },
      }),
    },
    commissionDatePaid: {
      label: "Date Paid",
      build: () => ({
        field: "commissionDatePaid",
        headerName: "Date Paid",
        width: 130,
        valueGetter: (value, row) => {
          const d = row?.commissionDatePaid || row?.lastPaymentAt || null;
          return d ? new Date(d).toLocaleDateString() : "—";
        },
      }),
    },
    invoiceUnpaid: {
      label: "Unpaid",
      build: () => ({
        field: "invoiceUnpaid",
        headerName: "Unpaid",
        width: 130,
        valueGetter: (value, row) => {
          const unpaid =
            row?.invoiceUnpaid ??
            row?.balance ??
            Math.max(
              0,
              Number(row?.invoiceTotal ?? row?.invoicedClient ?? 0) - Number(row?.invoicePaid ?? row?.paidToDate ?? 0)
            );
          return currency(unpaid);
        },
      }),
    },
    billedCosts: {
      label: "Billed Costs",
      build: () => ({
        field: "billedCosts",
        headerName: "Billed Costs",
        width: 220,
        renderCell: ({ row }) => {
          if (isManualFlatRow(row)) {
            const hasInvoiceContext = !!row?.invoiceId;
            return (
              <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <span>{hasInvoiceContext ? currency(row?.billedCosts ?? row?.billedActual ?? 0) : "—"}</span>
                <Chip size="small" variant="outlined" label={hasInvoiceContext ? "Manual adjustment" : "Manual entry"} color="default" />
              </Box>
            );
          }
          const billed = currency(row?.billedCosts ?? row?.billedCostsNoTax ?? row?.billedActual ?? 0);
          const status = row?.costStatus || 'unknown_estimate';
          const billedCount = Number(row?.billedPoCount ?? 0);
          const expectedCount = Number(row?.expectedPoCount ?? 0);
          const label = expectedCount > 0
            ? `${Math.min(billedCount, expectedCount)}/${expectedCount} bills`
            : (status === 'partial_costs' ? 'Partial costs' : (status === 'no_costs' ? 'No costs' : (status === 'complete_or_over' ? 'Costs complete' : 'Cost status')));
          const linkedBills = Array.isArray(row?.linkedBills) ? row.linkedBills : [];
          const expectedPoCosts = Array.isArray(row?.expectedPoCosts) ? row.expectedPoCosts : [];
          const estimatedRows = Array.isArray(row?.estimatedCostRows) ? row.estimatedCostRows : [];
          const fallbackEstimateRows = (!expectedPoCosts.length && estimatedRows.length)
            ? estimatedRows.map((r, i) => ({
                label: [r?.poNumber, r?.supplierName, r?.name || r?.sku].filter(Boolean).join(" · ") || `Estimate ${i + 1}`,
                cost: Number(r?.cost ?? 0),
              }))
            : [];
          const tooltip = (
            <Box sx={{ p: 0.5, maxWidth: 560 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Linked bills</Typography>
              {linkedBills.length ? linkedBills.map((b, i) => (
                <Typography key={`lb-${i}`} variant="caption" display="block">
                  {(b.poNumber || b.billNumber || `BILL-${b.billId || i + 1}`)} · {b.supplierName || 'Unknown Supplier'} · {currency(b.subtotalExTax)} ex tax · {currency(b.totalInclTax)} incl tax
                </Typography>
              )) : <Typography variant="caption" display="block">None entered</Typography>}
              <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.75, display: 'block' }}>Expected PO costs</Typography>
              {expectedPoCosts.length ? expectedPoCosts.map((p, i) => (
                <Typography key={`ep-${i}`} variant="caption" display="block">
                  {(p.poNumber || `PO-${i + 1}`)} · {p.supplierName || 'Unknown Supplier'} · {currency(p.estimatedCostExTax)} ex tax
                </Typography>
              )) : fallbackEstimateRows.length ? fallbackEstimateRows.map((p, i) => (
                <Typography key={`fer-${i}`} variant="caption" display="block">
                  {p.label} · {currency(p.cost)} ex tax
                </Typography>
              )) : ((Number(row?.estimatedCost || 0) > 0)
                ? <Typography variant="caption" display="block">Estimated total: {currency(row?.estimatedCost)} ex tax</Typography>
                : <Typography variant="caption" display="block">No PO estimate available</Typography>)}
            </Box>
          );
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span>{billed}</span>
              <Tooltip title={tooltip}>
                <Chip size="small" variant="outlined" label={label} color={status === 'complete_or_over' ? 'success' : (status === 'partial_costs' ? 'warning' : 'default')} />
              </Tooltip>
            </Box>
          );
        },
      }),
    },
    estimatedCost: {
      label: "Estimated Cost",
      build: () => ({
        field: "estimatedCost",
        headerName: "Estimated Cost",
        width: 150,
        valueGetter: (value, row) => (isManualFlatRow(row) ? (row?.invoiceId ? currency(row?.estimatedCost ?? 0) : "—") : currency(row?.estimatedCost ?? 0)),
      }),
    },
    billedMargin: {
      label: "Billed Margin",
      build: () => ({
        field: "billedMargin",
        headerName: "Billed Margin",
        width: 150,
        valueGetter: (value, row) => {
          if (isManualFlatRow(row)) return "—";
          const inv = Number(row?.actualRevenueNoTax ?? row?.invoiceTotal ?? row?.invoicedClient ?? 0);
          const costs = Number(row?.billedCosts ?? row?.billedCostsNoTax ?? row?.billedActual ?? 0);
          const margin = row?.billedMargin ?? Math.max(0, inv - costs);
          return currency(margin);
        },
      }),
    },
    marginPct: {
      label: "Margin",
      build: () => ({
        field: "marginPct",
        headerName: "Margin",
        width: 110,
        renderCell: ({ row }) => {
          if (isManualFlatRow(row)) {
            const commission = currency(row?.payoutAmount ?? row?.commissionAmount ?? row?.commissionDue ?? 0);
            return (
              <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <span>—</span>
                <span>{commission}</span>
              </Box>
            );
          }
          const subtotal = Number(row?.invoiceSubTotal ?? row?.actualRevenueNoTax ?? 0);
          const costs = Number(row?.billedCosts ?? row?.billedCostsNoTax ?? row?.billedActual ?? 0);
          const margin = Number(row?.billedMargin ?? Math.max(0, subtotal - costs));
          const pctNum = Number(row?.marginPct);
          const resolvedPct = Number.isFinite(pctNum)
            ? pctNum
            : (subtotal > 0 ? ((margin / subtotal) * 100) : null);
          const pct = resolvedPct == null ? "—" : `${resolvedPct.toFixed(2)}%`;
          const commission = currency(row?.commissionAmount ?? row?.commissionDue ?? 0);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
              <span>{pct}</span>
              <span>{commission}</span>
            </Box>
          );
        },
      }),
    },
    estimatedMargin: {
      label: "Estimated Margin",
      build: () => ({
        field: "estimatedMargin",
        headerName: "Estimated Margin",
        width: 150,
        valueGetter: (value, row) => currency(row?.estimatedMargin ?? ((Number(row?.invoiceSubTotal ?? 0) - Number(row?.estimatedCost ?? 0)))),
      }),
    },
    estimatedMarginPct: {
      label: "Estimated Margin %",
      build: () => ({
        field: "estimatedMarginPct",
        headerName: "Estimated Margin %",
        width: 150,
        valueGetter: (value, row) => {
          const pct = Number(row?.estimatedMarginPct);
          return Number.isFinite(pct) ? `${pct.toFixed(2)}%` : "—";
        },
      }),
    },
    commissionAmount: {
      label: "Commission Amount",
      build: () => ({
        field: "commissionAmount",
        headerName: "Commission Amount",
        width: 170,
        renderCell: ({ row }) => {
          const val = resolveDisplayPayoutAmount(row);
          const calc = Number(row.calculatedCommissionAmount ?? resolveFinalCalculatedPayout(row) ?? val);
          const isOver = hasExistingOverrideForRow(row);
          const overrideDisplay = resolveDisplayOverrideAmount(row);
          const diff = normalizeMoneyForDisplay(Number(val) - Number(calc));
          const isManual = String(row?.entryType || "").toLowerCase() === "manual" || !!row?.isManualCommission;
          const manualTitle = row?.manualCommissionMode === "rate"
            ? `Manual calculated commission | Basis: ${String(row?.basis || "margin")} | Rate: ${((Number(row?.rate || row?.calculatedCommissionRate || 0)) * 100).toFixed(2)}% | Amount: ${currency(val)}${row?.overrideNotes ? ` | Notes: ${row.overrideNotes}` : (row?.note ? ` | Notes: ${row.note}` : "")}`
            : `Manual commission | Amount: ${currency(val)}${row?.overrideNotes ? ` | Notes: ${row.overrideNotes}` : (row?.note ? ` | Notes: ${row.note}` : "")}`;
          const defaultTitle = `Calculated: ${currency(calc)} | Override: ${isOver ? currency(overrideDisplay ?? val) : "—"} | Difference: ${currency(diff)}${row?.overrideNotes ? ` | Notes: ${row.overrideNotes}` : ""}`;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontWeight: isOver ? 700 : 400, color: isOver ? "#c0181f" : "inherit" }}>
                <Tooltip title={isManual ? manualTitle : defaultTitle}><span>{currency(val)}</span></Tooltip>
              </span>
              {isOver && <Chip size="small" label="Overridden" color="warning" variant="outlined" />}
            </Box>
          );
        },
      }),
    },
    calculatedCommissionAmount: {
      label: "Calculated Commission",
      build: () => ({ field: "calculatedCommissionAmount", headerName: "Calculated Commission", width: 180, valueGetter: (v, row) => currency(row?.calculatedCommissionAmount ?? row?.liveComputedCommissionAmount ?? 0) }),
    },
    overrideAmount: {
      label: "Override Amount",
      build: () => ({ field: "overrideAmount", headerName: "Override Amount", width: 160, valueGetter: (v, row) => hasExistingOverrideForRow(row) ? currency(resolveDisplayOverrideAmount(row)) : "—" }),
    },
    overrideRate: {
      label: "Override %",
      build: () => ({ field: "overrideRate", headerName: "Override %", width: 130, valueGetter: (v, row) => row?.overrideRate != null ? `${(Number(row.overrideRate) * 100).toFixed(2)}%` : "—" }),
    },
    overrideBasis: {
      label: "Override Basis",
      build: () => ({ field: "overrideBasis", headerName: "Override Basis", width: 140, valueGetter: (v,row) => row?.overrideBasis || "—" }),
    },
    overrideNotes: {
      label: "Override Notes",
      build: () => ({ field: "overrideNotes", headerName: "Override Notes", width: 220, valueGetter: (v,row) => row?.overrideNotes || row?.commissionNotes || "—" }),
    },
    payoutAmountSource: {
      label: "Payout Source",
      build: () => ({ field: "payoutAmountSource", headerName: "Payout Source", width: 130, valueGetter: (v,row) => row?.payoutAmountSource || (row?.hasOverride ? "override" : "calculated") }),
    },
    overrideDifference: {
      label: "Difference",
      build: () => ({ field: "overrideDifference", headerName: "Difference", width: 130, valueGetter: (v,row) => currency((Number(row?.payoutAmount ?? row?.commissionAmount ?? 0)) - Number(row?.calculatedCommissionAmount ?? row?.commissionAmount ?? 0)) }),
    },
    commissionNotes: {
      label: "Commission Notes",
      build: () => ({
        field: "commissionNotes",
        headerName: "Commission Notes",
        width: 260,
        renderCell: ({ row }) => (
          <div
            title={row.commissionNotes || ""}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {row.commissionNotes || "—"}
          </div>
        ),
      }),
      financeOnly: true,
    },
    eventType: {
      label: "Event Type",
      build: () => ({ field: "eventType", headerName: "Event Type", width: 160, valueGetter: (v,row) => `${String(row?.entryType || "").toUpperCase()}${row?.sourceType ? ` / ${row.sourceType}` : ""}` }),
    },
    eventDate: {
      label: "Event Date",
      build: () => ({ field: "eventDate", headerName: "Event Date", width: 150, valueGetter: (v,row) => row?.eventDate ? new Date(row.eventDate).toLocaleDateString() : "—" }),
    },
    eventAmount: {
      label: "Event Amount",
      build: () => ({ field: "eventAmount", headerName: "Event Amount", width: 140, valueGetter: (v,row) => currency(row?.eventAmount || 0) }),
    },
    lockState: {
      label: "State",
      build: () => ({
        field: "lockState",
        headerName: "State",
        width: 120,
        renderCell: ({ row }) => {
          const s = String(row.lockState || "DRAFT").toUpperCase();
          const color = s === "PAID" ? "success" : s === "APPROVED" ? "primary" : "default";
          return <Chip size="small" label={s} color={color} />;
        },
      }),
    },
    readyToPay: {
      label: "Review / Payment",
      build: () => ({
        field: "readyToPay",
        headerName: "Review / Payment",
        width: 360,
        sortable: false,
        renderCell: ({ row }) => {
          const reasons = (row.readinessReasons || [])
            .map((reason) => READINESS_REASON_LABELS[reason] || reason)
            .join(" • ");
          const paidStatus = String(row?.paymentStatus || "").toLowerCase();
          const isPaidLike = paidStatus === "paid" || paidStatus === "exported";
          const reviewChip = isPaidLike
            ? null
            : row.readyToPay
            ? <Chip size="small" color="success" label="Ready to Review" />
            : <Chip size="small" color="warning" variant="outlined" label="Not Ready" />;
          const paymentMeta = getPaymentChipMeta(row);
          const paymentChip = <Chip size="small" color={paymentMeta.color} variant={paymentMeta.variant} label={paymentMeta.label} />;
          const guidance = "Invoice is paid and commission can be reviewed. Supplier costs may still be incomplete.";
          const paidAmt = Number(row?.amountPaid ?? row?.paidToDate ?? 0);
          const totalAmt = Number(row?.totalCommissionAmount ?? row?.commissionDue ?? row?.commissionAmount ?? 0);
          const remainingAmt = Number(row?.balanceRemaining ?? row?.balance ?? Math.max(0, totalAmt - paidAmt));
          const paidDate = row?.paidAt || row?.commissionDatePaid || row?.lastPaymentAt || null;
          const reference = row?.paymentReference || row?.paymentBatchId || (Array.isArray(row?.paymentBatchIds) ? row.paymentBatchIds[0] : null) || null;
          const exportRef = row?.exportFileName || reference || null;
          const tooltipLines = [
            row.readyToPay ? "Review: Ready to Review" : "Review: Not Ready",
            `Payment: ${paymentMeta.label}`,
            `Amount paid: ${currency(paidAmt)}`,
            `Balance remaining: ${currency(remainingAmt)}`,
            paidDate ? `Paid date: ${new Date(paidDate).toLocaleDateString()}` : "Paid date: —",
            row?.paidByName ? `Paid by: ${row.paidByName}` : (row?.paidById ? `Paid by user #${row.paidById}` : "Paid by: —"),
            reference ? `Batch/reference: ${reference}` : "Batch/reference: —",
            row?.paymentStatus === "exported" ? `Export: ${exportRef || "Exported"}` : "Export: Not exported",
            row?.commissionNotes ? `Notes: ${row.commissionNotes}` : null,
            row?.isLocked ? `Locked by ${row?.lockedByName || (row?.lockedById ? `user #${row.lockedById}` : "Finance")}${row?.lockedAt ? ` on ${new Date(row.lockedAt).toLocaleDateString()}` : ""}. Reason: ${row?.lockedReason || "Payroll finalized."}` : null,
            `History events: ${Array.isArray(row?.paymentHistory) ? row.paymentHistory.length : 0}`,
            ...((Array.isArray(row?.paymentHistory) ? row.paymentHistory.slice(-4) : []).map((ev) => {
              const d = ev?.eventDate ? new Date(ev.eventDate).toLocaleDateString() : "—";
              const who = ev?.userName || (ev?.userId ? `user #${ev.userId}` : "system");
              const amt = ev?.amount != null ? currency(ev.amount) : "—";
              return `• ${String(ev?.eventType || "event")} ${d} ${amt} by ${who}${ev?.reference ? ` (${ev.reference})` : ""}`;
            })),
            !row.readyToPay ? `${reasons || "Not ready to pay"}${reasons ? "\n" : ""}${guidance}` : guidance,
          ].filter(Boolean).join("\n");
          return (
            <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltipLines}</span>}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.25 }}>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {reviewChip}
                  {paymentChip}
                </Box>
                {String(row?.paymentStatus || "").toLowerCase() === "partial" && (
                  <Typography variant="caption" color="text.secondary">
                    {currency(paidAmt)} / {currency(totalAmt)} paid • {currency(remainingAmt)} remaining
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      }),
    },
    flags: {
      label: "Flags",
      build: () => ({
        field: "flags",
        headerName: "Flags",
        width: 300,
        renderCell: ({ row }) => {
          const f = [];
          if (row.missingInvoices) f.push(<Chip key="mi" size="small" label="⚠ Missing invoices" variant="outlined" />);
          if (row.missingBills) f.push(<Chip key="mb" size="small" label="⚠ Missing bills" variant="outlined" />);
          if (row.overTarget) f.push(<Chip key="ot" size="small" label="⚠ Over target" variant="outlined" />);
          if (!f.length) return <span>—</span>;

          const reasons = Array.isArray(row.readinessReasons) ? row.readinessReasons : [];
          const nextSteps = reasons
            .map((code) => `• ${READINESS_REASON_NEXT_STEP[code] || `Resolve: ${code}`}`)
            .join("\n");

          const tooltipLines = [
            row.readyToPay ? "Ready to pay." : "Not ready to pay.",
            ...reasons.map((code) => `• ${READINESS_REASON_LABELS[code] || code}`),
            ...(nextSteps ? ["", "Next steps:", ...nextSteps.split("\n")] : []),
          ].join("\n");

          return (
            <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltipLines}</span>}>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>{f}</Box>
            </Tooltip>
          );
        },
      }),
    },
  };

  const columnKeysAll = useMemo(
    () =>
      Object.entries(COLUMN_DEFS)
        .filter(([_, def]) => (def.financeOnly ? financeUser : true))
        .map(([k]) => k),
    [financeUser]
  );

  const DEFAULT_VISIBLE = useMemo(() => {
    const base = [
      'rep',
      'orderId',
      'client',
      'invoiceNumber',
      'invoiceSubTotal',
      'billedCosts',
      'estimatedCost',
      'marginPct',
      'readyToPay',
      'commissionAmount',
      'actions',
    ];
    return base;
  }, [financeUser]);

  const columnsStorageKey = (id) => `reports.commissions.columns.v5.${id}`;
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [bulkDialogMode, setBulkDialogMode] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackAction, setFeedbackAction] = useState(null);
  const [readinessPolicy, setReadinessPolicy] = useState({
    requireInvoicePaid: true,
    requireInvoiceFinalized: true,
    requireMarginFinalized: true,
    requireAssignedPlan: true,
  });

  const defaultHiddenColumns = useMemo(
    () => columnKeysAll.filter((key) => !DEFAULT_VISIBLE.includes(key)),
    [DEFAULT_VISIBLE, columnKeysAll],
  );

  useEffect(() => {
    const key = columnsStorageKey(instanceUniqueId);
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const visibleColumnKeys = JSON.parse(saved);
        if (Array.isArray(visibleColumnKeys) && visibleColumnKeys.length) {
          const visibleSet = new Set(visibleColumnKeys.map(String));
          setHiddenColumns(columnKeysAll.filter((columnKey) => !visibleSet.has(String(columnKey))));
          return;
        }
      } catch {
        // fall through to defaults
      }
    }
    setHiddenColumns(defaultHiddenColumns);
  }, [columnKeysAll, defaultHiddenColumns, instanceUniqueId]);

  useEffect(() => {
    const key = columnsStorageKey(instanceUniqueId);
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    const visibleColumnKeys = columnKeysAll.filter((columnKey) => !hiddenSet.has(String(columnKey)));
    try {
      localStorage.setItem(key, JSON.stringify(visibleColumnKeys));
    } catch {
      // ignore persistence failure
    }
  }, [columnKeysAll, hiddenColumns, instanceUniqueId]);

  const columnVisibilityColumns = useMemo(
    () => columnKeysAll.map((key) => ({
      field: key,
      headerName: COLUMN_DEFS[key]?.label || key,
    })),
    [columnKeysAll],
  );

  const baseColumns = useMemo(() => {
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    return columnKeysAll
      .filter((key) => !hiddenSet.has(String(key)))
      .filter((k) => COLUMN_DEFS[k])
      .map((k) => COLUMN_DEFS[k].build());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeysAll, hiddenColumns, rows, financeUser, repType]);

  const csvEscape = (value) => {
    if (value == null) return "";
    const str = String(value).replace(/\r?\n|\r/g, " ").trim();
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };

  const formatRepNames = (list = []) =>
    Array.isArray(list)
      ? list
          .map((u) => [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || u?.id)
          .filter(Boolean)
          .join(", ")
      : "";

  const csvNumber = (...values) => {
    for (const value of values) {
      if (value == null || value === "") continue;
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  const csvMoney = (...values) => csvNumber(...values).toFixed(2);
  const csvDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  };
  const csvYesNo = (value) => (value ? "Yes" : "No");

  const getCsvValue = (key, row = {}) => {
    switch (key) {
      case "date":
        return csvDate(row.invoiceDate || row.soDate || row.projectDate || row.createdAt);
      case "clientRepName":
        return formatRepNames(row.clientRepDetails);
      case "orderRepName":
        return formatRepNames(row.orderRepDetails);
      case "rep":
        return row.repDisplayName || formatRepNames(row.assignedRepDetails) || formatRepNames(repType === "order" ? row.orderRepDetails : row.clientRepDetails) || "";
      case "orderId":
        return row.orderNumber || row.orderId || row.OrderId || "";
      case "client":
        return row.businessName || row.clientName || row.customerName || row.ClientName || row?.customer?.businessName || "";
      case "projectTitle":
        return row.projectTitle || row.orderTitle || "";
      case "invoiceNumber":
        return Array.isArray(row.invoiceNumbers) ? row.invoiceNumbers.join(", ") : row.invoiceNumber || "";
      case "invoiceSubTotal":
        return isManualFlatRow(row) && !row.invoiceId ? "" : csvMoney(row.invoiceSubTotal, row.actualRevenueNoTax, row.soRevenueSigned, row.subTotal);
      case "invoiceTax":
        return isManualFlatRow(row) ? "" : csvMoney(row.invoiceTax, row.taxTotal);
      case "invoiceTotal":
        return isManualFlatRow(row) ? "" : csvMoney(row.invoiceTotal, row.invoicedClient);
      case "invoicePaid":
        return isManualFlatRow(row) ? "" : csvMoney(row.invoicePaid, row.paidToDate);
      case "invoiceUnpaid": {
        const total = csvNumber(row.invoiceTotal, row.invoicedClient);
        const paid = csvNumber(row.invoicePaid, row.paidToDate);
        return csvMoney(row.invoiceUnpaid, row.balance, Math.max(0, total - paid));
      }
      case "invoiceDatePaid":
        return csvDate(row.invoiceDatePaid);
      case "commissionDatePaid":
        return csvDate(row.commissionDatePaid || row.paidAt || row.lastPaymentAt || row.lastPaidAt);
      case "billedCosts":
        return csvMoney(row.billedCosts, row.actualCost, row.billedCostTotal, row.costsBilled);
      case "estimatedCost":
        return csvMoney(row.estimatedCost, row.estimatedCosts, row.expectedCost, row.soCostSigned);
      case "billedMargin":
        return csvMoney(row.billedMargin, row.marginAmount, csvNumber(row.invoiceSubTotal, row.actualRevenueNoTax, row.soRevenueSigned, row.subTotal) - csvNumber(row.billedCosts, row.actualCost, row.billedCostTotal, row.costsBilled));
      case "marginPct":
        return percent(row.marginPct ?? row.marginPercent ?? row.billedMarginPct ?? 0);
      case "commissionAmount":
        return csvMoney(row.payoutAmount, row.commissionAmount, row.commissionDue, row.totalCommissionAmount);
      case "calculatedCommission":
        return csvMoney(row.calculatedCommission, row.calculatedCommissionAmount, row.ruleCommissionAmount, row.commissionDue);
      case "overrideAmount":
        return row.overrideAmount == null ? "" : csvMoney(row.overrideAmount);
      case "overridePercent":
      case "overridePct":
        return row.overridePercent == null && row.overridePct == null ? "" : percent(row.overridePercent ?? row.overridePct);
      case "overrideBasis":
        return row.overrideBasis || row.overrideReason || "";
      case "difference":
        return csvMoney(row.difference, row.commissionDifference, csvNumber(row.payoutAmount, row.commissionAmount, row.commissionDue) - csvNumber(row.calculatedCommission, row.calculatedCommissionAmount, row.ruleCommissionAmount));
      case "payoutSource":
        return row.payoutAmountSource || row.payoutSource || row.source || "";
      case "commissionNotes":
        return row.commissionNotes || row.notes || "";
      case "readyToPay":
        return csvYesNo(row.readyToPay);
      case "paymentStatus":
        return row.paymentStatus || row.commissionStatus || "";
      case "flags": {
        const flags = [];
        if (row.missingInvoices) flags.push("Missing invoices");
        if (row.missingBills) flags.push("Missing bills");
        if (row.overTarget) flags.push("Over target");
        if (Array.isArray(row.readinessReasons)) flags.push(...row.readinessReasons.map((code) => READINESS_REASON_LABELS[code] || code));
        return [...new Set(flags)].join("; ");
      }
      default: {
        const value = row[key];
        if (Array.isArray(value)) return value.join(", ");
        if (value && typeof value === "object") return JSON.stringify(value);
        return value ?? "";
      }
    }
  };

  const downloadCurrentCsv = () => {
    try {
      const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
      const exportKeys = columnKeysAll.filter((key) => !hiddenSet.has(String(key)) && COLUMN_DEFS[key]);
      const headers = exportKeys.map((key) => COLUMN_DEFS[key]?.label || key);
      const csvRows = (visibleRows || []).map((row) =>
        exportKeys.map((key) => csvEscape(getCsvValue(key, row))).join(",")
      );
      const csv = [headers.map(csvEscape).join(","), ...csvRows].join("\r\n");
      const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commissions-report-${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("CSV download failed. Please try again or adjust the visible columns.");
    }
  };

  const actionsCol = useMemo(() => {
    return {
      field: "_actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      renderCell: ({ row }) => {
        const rowPaidOrFinalized = isCommissionRowPaidOrFinalized(row);
        const canEdit = financeUser && !!row?.canEditCommissionOverride && !rowPaidOrFinalized;
        const disabledReason = rowPaidOrFinalized
          ? "Unpay this commission before editing the override."
          : (row?.editDisabledReason || (financeUser ? "Cannot edit this commission row" : "View only"));
        const routeOrderId = getSalesOrderRouteId(row);
        const url = routeOrderId ? `/instance/${instanceUniqueId}/sales/orders/${routeOrderId}/commissions` : null;
        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip title={canEdit ? "Edit commission override" : disabledReason}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    if (!canEdit) return;
                    setEditRow(row);
                    setEditOpen(true);
                  }}
                  disabled={!canEdit}
                  sx={{ textTransform: "none" }}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
            {url && (
              <Button
                size="small"
                variant="text"
                endIcon={<OpenInNewIcon />}
                onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                sx={{ textTransform: "none" }}
              >
                Order
              </Button>
            )}
            <Tooltip title="View payment notes & history">
              <span>
                <IconButton size="small" onClick={() => setHistoryRow(row)}>
                  <ReceiptLongIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    };
  }, [financeUser, instanceUniqueId]);

  const columns = useMemo(() => {
    return [...baseColumns, actionsCol];
  }, [baseColumns, actionsCol]);

  const commissionTableMinWidth = useMemo(
    () => columns.reduce((total, column) => total + (column.width ?? column.minWidth ?? 160), 50),
    [columns],
  );

  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: buildCurrentFilters(),
    hiddenColumns,
  }), [buildCurrentFilters, hiddenColumns]);

  const loadCommissions = useCallback(async (filtersOverride = null) => {
    const effectiveFilters = filtersOverride || buildCurrentFilters();
    setLoading(true);
    try {
      const { repId: effectiveRepId, repType: effectiveRepType } = repScopedViewer
        ? {
            repId: currentUserId || undefined,
            repType: isOrderRepViewer ? "order" : "client",
          }
        : {
            repId: effectiveFilters.repId || undefined,
            repType: effectiveFilters.repType || undefined,
          };
      const params = {
        instanceUniqueId,
        instanceId: instanceId || undefined,
        repId: effectiveRepId,
        repType: effectiveRepType,
        commissionState: effectiveFilters.state || undefined,
        paidStatus: effectiveFilters.paidStatus || undefined,
        invoiceStatus: effectiveFilters.invoiceStatus || undefined,
        billStatus: effectiveFilters.billStatus || undefined,
        commissionStatus: effectiveFilters.commissionStatus || undefined,
        client: effectiveFilters.client || undefined,
        dateFrom: effectiveFilters.dateFrom || undefined,
        dateTo: effectiveFilters.dateTo || undefined,
        invoiceDatePaidFrom: effectiveFilters.invoiceDatePaidFrom || undefined,
        invoiceDatePaidTo: effectiveFilters.invoiceDatePaidTo || undefined,
        commissionDatePaidFrom: effectiveFilters.commissionDatePaidFrom || undefined,
        commissionDatePaidTo: effectiveFilters.commissionDatePaidTo || undefined,
      };
      const data = await fetchCommissionsReport(params);
      setRawRows(data?.rows || []);
      setInstanceId((prev) => prev ?? data?.instanceId ?? data?.rows?.[0]?.instanceId ?? null);
      setHasRunReport(true);
    } catch (e) {
      console.error("Commissions report load failed", e);
      setRawRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    buildCurrentFilters,
    currentUserId,
    instanceId,
    instanceUniqueId,
    isOrderRepViewer,
    repScopedViewer,
  ]);

  const runFetch = useCallback(async () => {
    await loadCommissions();
  }, [loadCommissions]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const nextFilters = buildValidatedCommissionFilters(savedState);
    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: columnKeysAll,
      fallbackHiddenColumns: defaultHiddenColumns,
    });

    setHiddenColumns(nextHiddenColumns);
    applyFilterState(nextFilters);
    setSelected([]);
    await loadCommissions(nextFilters);
  }, [
    applyFilterState,
    buildValidatedCommissionFilters,
    columnKeysAll,
    defaultHiddenColumns,
    loadCommissions,
  ]);

  const filterExcludesPaidAfterPay = () => (paidStatus === "unpaid" || commissionStatus === "unpaid");
  const filterExcludesUnpaidAfterUnpay = () => (paidStatus === "paid" || commissionStatus === "paid");
  const hasExtraNarrowingFilters = () => Boolean(
    readyFilter !== "all" || dateFrom || dateTo || invoiceDatePaidFrom || invoiceDatePaidTo || commissionDatePaidFrom || commissionDatePaidTo
  );

  useEffect(() => {
    setHasRunReport(false);
    setRawRows([]);
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!repScopedViewer) return;
    setRepId(currentUserId);
    setRepType(isOrderRepViewer ? "order" : "client");
  }, [repScopedViewer, currentUserId, isOrderRepViewer]);

  const handleResetFilters = useCallback(() => {
    applyFilterState(getDefaultCommissionFilters());
    setSelected([]);
  }, [applyFilterState, getDefaultCommissionFilters]);

  const summaryCards = useMemo(() => ([
    {
      key: "commission-rows",
      label: "Visible Rows",
      value: visibleRows.length,
      tone: "count",
      helperText: "Commissionable financial events matching the current filters.",
    },
    {
      key: "commission-ready",
      label: "Ready to Review",
      value: visibleRows.filter((row) => row.readyToPay).length,
      tone: "count",
      helperText: "Rows currently marked ready for finance review and payout processing.",
    },
    {
      key: "commission-invoice-total",
      label: "Invoice Total",
      value: currency(summary.invoiceTotal),
      tone: "sales",
      helperText: "Total invoice face value across the current visible commission rows.",
    },
    {
      key: "commission-margin-total",
      label: "Gross Margin Total",
      value: currency(summary.billedMargin),
      tone: Number(summary.billedMargin) < 0 ? "danger" : "margin",
      helperText: "Billed gross margin represented by the current visible rows.",
    },
    {
      key: "commission-margin-average",
      label: "Gross Margin Average",
      value: currency(summary.avgMargin),
      tone: "count",
      helperText: "Average billed gross margin per visible commission row.",
    },
    {
      key: "commission-total",
      label: "Sales Rep Commission Total",
      value: currency(summary.commissionDue),
      tone: "budget",
      helperText: "Current payout amount across the filtered commission rows.",
    },
    {
      key: "commission-calculated-total",
      label: "Calculated Total",
      value: currency(summary.calculatedCommission),
      tone: "revenue",
      helperText: "System-calculated commission total before any overrides.",
    },
    {
      key: "commission-override-delta",
      label: "Override Delta",
      value: currency(summary.overrideDelta),
      tone: Number(summary.overrideDelta) === 0 ? "count" : (Number(summary.overrideDelta) > 0 ? "warning" : "danger"),
      helperText: "Difference between payout amounts and system-calculated commission.",
    },
  ]), [summary, visibleRows]);

  return (
    <div className="instance-dashboard instance-orders-page">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Tooltip title="Go back">
                    <IconButton size="small" onClick={() => history.goBack()} sx={{ color: "text.secondary" }}>
                      <ArrowBackIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                    Commissions Report
                  </Typography>
                  <Chip
                    label={loading ? "Loading..." : `${hasRunReport ? visibleRows.length : 0} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label="commissions"
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-info-bg)",
                      color: "var(--ec-status-info-text)",
                      border: "1px solid var(--ec-status-info-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="commissions"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columnVisibilityColumns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Row density">
                    <span>
                      <IconButton size="small" disabled sx={{ color: "text.secondary" }}>
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={downloadCurrentCsv}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>

            <Box sx={{ ...reportContentSx, minWidth: 0, maxWidth: "100%" }}>

            {/* FILTER BAR */}
            <Paper elevation={0} sx={{ ...reportFilterCardSx, mb: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Commissions Report Controls
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Filter commissionable events by rep, client, readiness, paid state, invoice timing, and commission status.
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  label="Search project/client/project#/PO#"
                  value={quickFind}
                  onChange={(e) => setQuickFind(e.target.value)}
                  sx={{ minWidth: 320, flex: "1 1 320px", maxWidth: 520 }}
                />
              </Box>

              <Box sx={reportFilterGridSx}>
                <FormControl fullWidth>
                  <InputLabel id="rep-id-label">Rep</InputLabel>
                  <Select labelId="rep-id-label" value={repId} label="Rep" onChange={(e) => setRepId(e.target.value)} disabled={repScopedViewer}>
                    {!repScopedViewer && <MenuItem value="">All Active</MenuItem>}
                    {repOptions.active.map((r) => <MenuItem key={`rep-${r.id}`} value={r.id}>{r.label}</MenuItem>)}
                    {repOptions.inactive.length > 0 ? <MenuItem disabled>— Inactive Users —</MenuItem> : null}
                    {repOptions.inactive.map((r) => <MenuItem key={`rep-inactive-${r.id}`} value={r.id}>{r.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Invoice Date From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Invoice Date To"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Invoice Paid Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={invoiceDatePaidFrom}
                  onChange={(e) => setInvoiceDatePaidFrom(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Date Paid"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={commissionDatePaidFrom}
                  onChange={(e) => setCommissionDatePaidFrom(e.target.value)}
                />
                <FormControl fullWidth>
                  <InputLabel id="rep-type-label">Rep Type</InputLabel>
                  <Select
                    labelId="rep-type-label"
                    value={repType}
                    label="Rep Type"
                    onChange={(e) => setRepType(e.target.value)}
                    disabled={repScopedViewer}
                  >
                    <MenuItem value="client">Client Rep</MenuItem>
                    <MenuItem value="order">Order Rep</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Client" value={client} onChange={(e) => setClient(e.target.value)} fullWidth />
              </Box>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                <Chip label="Invoices: Both" color={invoiceStatus === 'both' ? 'info' : 'default'} onClick={() => setInvoiceStatus('both')} clickable />
                <Chip label="Invoices: Paid" color={invoiceStatus === 'paid' ? 'info' : 'default'} onClick={() => setInvoiceStatus('paid')} clickable />
                <Chip label="Invoices: Unpaid" color={invoiceStatus === 'unpaid' ? 'info' : 'default'} onClick={() => setInvoiceStatus('unpaid')} clickable />

                <Chip label="Bills: Both" color={billStatus === 'both' ? 'warning' : 'default'} onClick={() => setBillStatus('both')} clickable />
                <Chip label="Bills: Paid" color={billStatus === 'paid' ? 'warning' : 'default'} onClick={() => setBillStatus('paid')} clickable />
                <Chip label="Bills: Unpaid" color={billStatus === 'unpaid' ? 'warning' : 'default'} onClick={() => setBillStatus('unpaid')} clickable />

                <Chip label="Commissions: Both" color={commissionStatus === 'both' ? 'success' : 'default'} onClick={() => setCommissionStatus('both')} clickable />
                <Chip label="Commissions: Paid" color={commissionStatus === 'paid' ? 'success' : 'default'} onClick={() => setCommissionStatus('paid')} clickable />
                <Chip label="Commissions: Unpaid" color={commissionStatus === 'unpaid' ? 'success' : 'default'} onClick={() => setCommissionStatus('unpaid')} clickable />
              </Box>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                <Button variant="outlined" onClick={() => setShowMoreFilters((v) => !v)}>{showMoreFilters ? 'Hide Filters' : 'More Filters'}</Button>
                <Button variant="outlined" onClick={handleResetFilters}>Reset</Button>
                <Button variant="contained" onClick={runFetch} disabled={loading}>
                  Get Report
                </Button>
                {financeUser ? (
                  <Button variant="outlined" onClick={() => setPolicyDialogOpen(true)}>
                    Readiness Policy
                  </Button>
                ) : null}
              </Box>

              {showMoreFilters && (
                <Box sx={{ ...reportFilterGridSx, mt: 1 }}>
                  <TextField label="Invoice Paid Date To" type="date" InputLabelProps={{ shrink: true }} value={invoiceDatePaidTo} onChange={(e) => setInvoiceDatePaidTo(e.target.value)} fullWidth />
                  <TextField label="Date Paid To" type="date" InputLabelProps={{ shrink: true }} value={commissionDatePaidTo} onChange={(e) => setCommissionDatePaidTo(e.target.value)} fullWidth />
                  <FormControl fullWidth>
                    <InputLabel id="paid-label">Paid Status</InputLabel>
                    <Select labelId="paid-label" value={paidStatus} label="Paid Status" onChange={(e) => setPaidStatus(e.target.value)}>
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="unpaid">Unpaid</MenuItem>
                      <MenuItem value="partial">Partially Paid</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="state-label">Commission State</InputLabel>
                    <Select labelId="state-label" value={state} label="Commission State" onChange={(e) => setState(e.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="APPROVED">Approved</MenuItem>
                      <MenuItem value="PAID">Paid</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Rows are commissionable financial events. A project can have multiple rows when multiple invoices are paid, adjustments are made, or costs change after payout.
                </Typography>

              {hasRunReport && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="All"
                    color={readyFilter === 'all' ? 'primary' : 'default'}
                    variant={readyFilter === 'all' ? 'filled' : 'outlined'}
                    onClick={() => setReadyFilter('all')}
                  />
                  <Chip
                    label={`Ready (${rowsForViewer.filter((r) => r.readyToPay).length})`}
                    color={readyFilter === 'ready' ? 'success' : 'default'}
                    variant={readyFilter === 'ready' ? 'filled' : 'outlined'}
                    onClick={() => setReadyFilter('ready')}
                  />
                  <Chip
                    label={`Not Ready (${rowsForViewer.filter((r) => !r.readyToPay).length})`}
                    color={readyFilter === 'not_ready' ? 'warning' : 'default'}
                    variant={readyFilter === 'not_ready' ? 'filled' : 'outlined'}
                    onClick={() => setReadyFilter('not_ready')}
                  />
                </Box>
              )}
            </Paper>

            {hasRunReport ? (
              <Box sx={{ ...reportSummaryGridSx, mb: 1.5 }}>
                {summaryCards.map((card) => (
                  <ReportSummaryCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    tone={card.tone}
                    helperText={card.helperText}
                  />
                ))}
              </Box>
            ) : null}

            {hasRunReport ? (
              <>
            {financeUser && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Tooltip title={bulkProcessing ? "Processing selected commissions..." : (payDisabledReason || (selectedBlockedRows.length ? "Some selected rows will be skipped. Review confirmation." : (selectedWarningRows.length ? "Some selected rows have incomplete costs. Confirmation required." : "Marks selected commissions paid. No file download.")))}>
                <span><Button variant="contained" disabled={bulkProcessing || !!payDisabledReason} onClick={() => setBulkDialogMode("pay")}>{bulkProcessing ? "Processing..." : "Pay Selected"}</Button></span>
              </Tooltip>
              <Tooltip title={bulkProcessing ? "Processing selected commissions..." : (payDisabledReason || (selectedBlockedRows.length ? "Some selected rows will be skipped. Review confirmation." : (selectedWarningRows.length ? "Some selected rows have incomplete costs. Confirmation required." : "Marks selected commissions paid and downloads payout spreadsheet.")))}>
                <span><Button variant="outlined" disabled={bulkProcessing || !!payDisabledReason} onClick={() => setBulkDialogMode("pay_export_xlsx")}>{bulkProcessing ? "Processing..." : "Pay + Export XLSX"}</Button></span>
              </Tooltip>
              <Tooltip title={!selected.length ? "No selected rows" : (selectedLinkedPayments.length === 0 ? "No active payments to reverse." : "Choose linked payment rows to reverse.")}>
                <span><Button variant="outlined" disabled={!selected.length || selectedLinkedPayments.length === 0 || bulkProcessing} onClick={() => { setReversePaymentIds(selectedLinkedPayments.length === 1 ? [Number(selectedLinkedPayments[0].id)] : []); setReverseReason(""); setReversePickerOpen(true); }}>Un-Pay Selected Commissions</Button></span>
              </Tooltip>
            </Box>
            )}
            {selected.length > 0 && (
              <Paper sx={{ p: 1.25, mb: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    <strong>{selectedRows.length} selected</strong>
                    &nbsp;•&nbsp; Selected commission total: {currency(selectedCommissionTotal)}
                    &nbsp;•&nbsp; Selected calculated total: {currency(selectedCalculatedTotal)}
                    &nbsp;•&nbsp; Override delta: {currency(selectedOverrideDelta)}
                    &nbsp;•&nbsp; Ready: {selectedReadyCount}
                    &nbsp;•&nbsp; Not ready: {selectedNotReadyCount}
                    &nbsp;•&nbsp; Paid: {selectedPaidCount}
                    &nbsp;•&nbsp; Unpaid: {selectedUnpaidCount}
                  </Typography>
                  <Button size="small" onClick={() => setSelected([])}>Clear Selection</Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Actions apply to selected commission rows only.
                </Typography>
                {process.env.NODE_ENV !== "production" && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    debug: selected={JSON.stringify(selected)} • selectedRows={selectedRows.length} • selectedEventIds={JSON.stringify(selectedEventIds)} • instanceId={String(instanceId || "null")} • selectedNotReadyCount={selectedNotReadyCount}
                  </Typography>
                )}
              </Paper>
            )}
            <BulkCommissionActionDialog
              open={!!bulkDialogMode}
              mode={bulkDialogMode}
              onClose={() => setBulkDialogMode(null)}
              selectedRows={selectedRows}
              selectedPaymentIds={selectedPaymentIds}
              selectedBatchIds={selectedBatchIds}
              defaultDate={new Date().toISOString().slice(0, 10)}
              processing={bulkProcessing}
              onConfirm={async ({ paymentDate, reference, notes, reason, manualPayNowAmount, ackIncomplete }) => {
                if (bulkProcessing) return;
                setBulkProcessing(true);
                try {
                  if (bulkDialogMode === "pay") {
                    const manualPayNowAmounts = {};
                    const paymentContexts = {};
                    selectedRows.forEach((r) => {
                      if (isIncompleteCostRow(r) && r?.commissionPayoutEventId) {
                        const key = String(r.commissionPayoutEventId);
                        manualPayNowAmounts[key] = Number(manualPayNowAmount || 0);
                        paymentContexts[key] = {
                          costStatus: r?.costStatus || null,
                          billedPoCount: Number(r?.billedPoCount || 0),
                          expectedPoCount: Number(r?.expectedPoCount || 0),
                          actualBilledCostsExTax: Number(r?.totalLiveBilledCostsExTax ?? r?.billedCosts ?? 0),
                          estimatedCostExTax: Number(r?.totalEstimatedCostExTax ?? r?.estimatedCost ?? 0),
                          suggestedPrepaymentAmount: Number(getSuggestedPrepaymentAmount(r) || 0),
                          liveCommission: Number(getLiveCommission(r) || 0),
                          costCompletionRatio: Number(getCostCompletionRatio(r) || 0),
                        };
                      }
                    });
                    const useManualMode = manualPayNowAmount !== "" && Number(manualPayNowAmount) > 0;
                    const res = await paySelectedCommissions({ instanceId, commissionPayoutEventIds: selectedPayableEventIds, rowKeys: selected, repType, paidAt: paymentDate, reference, note: notes, manualPayNowAmounts, paymentContexts, acknowledgeIncompleteCosts: !!ackIncomplete, acknowledgement: !!ackIncomplete, paymentMode: useManualMode ? "manual_override" : "live_actuals" });
                    const data = res?.data || {};
                    const processed = Array.isArray(data.processed) ? data.processed : [];
                    const skipped = Array.isArray(data.skipped) ? data.skipped : [];
                    const failed = Array.isArray(data.failed) ? data.failed : [];
                    setActionResult({ action: "pay", processed, skipped, failed });
                    setFeedbackMessage(`${processed.length} commissions paid. ${skipped.length} skipped. ${failed.length} failed.`);
                    setFeedbackAction(null);
                    const processedIds = new Set(processed.map((p) => `ev:${String(p.id)}`));
                    setSelected((prev) => prev.filter((id) => !processedIds.has(String(id))));
                  } else if (bulkDialogMode === "pay_export_xlsx") {
                    const manualPayNowAmounts = {};
                    const paymentContexts = {};
                    selectedRows.forEach((r) => {
                      if (isIncompleteCostRow(r) && r?.commissionPayoutEventId) {
                        const key = String(r.commissionPayoutEventId);
                        manualPayNowAmounts[key] = Number(manualPayNowAmount || 0);
                        paymentContexts[key] = {
                          costStatus: r?.costStatus || null,
                          billedPoCount: Number(r?.billedPoCount || 0),
                          expectedPoCount: Number(r?.expectedPoCount || 0),
                          actualBilledCostsExTax: Number(r?.totalLiveBilledCostsExTax ?? r?.billedCosts ?? 0),
                          estimatedCostExTax: Number(r?.totalEstimatedCostExTax ?? r?.estimatedCost ?? 0),
                          suggestedPrepaymentAmount: Number(getSuggestedPrepaymentAmount(r) || 0),
                          liveCommission: Number(getLiveCommission(r) || 0),
                          costCompletionRatio: Number(getCostCompletionRatio(r) || 0),
                        };
                      }
                    });
                    const useManualMode = manualPayNowAmount !== "" && Number(manualPayNowAmount) > 0;
                    const xlsxRes = await payCommissionsExportXlsx({ instanceId, commissionPayoutEventIds: selectedPayableEventIds, rowKeys: selected, repType, paidAt: paymentDate, reference, note: notes, manualPayNowAmounts, paymentContexts, acknowledgeIncompleteCosts: !!ackIncomplete, acknowledgement: !!ackIncomplete, paymentMode: useManualMode ? "manual_override" : "live_actuals" });
                    const url = window.URL.createObjectURL(new Blob([xlsxRes.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `commission-payouts-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                    setFeedbackMessage(`${selectedPayableEventIds.length} commissions marked as paid and XLSX exported.`);
                    setFeedbackAction(null);
                  } else if (bulkDialogMode === "unpay") {
                    const res = await unpayCommissions({ paymentIds: reversePaymentIds, reason });
                    const data = res?.data || {};
                    const processed = Array.isArray(data.processed) ? data.processed : [];
                    const skipped = Array.isArray(data.skipped) ? data.skipped : [];
                    const failed = Array.isArray(data.failed) ? data.failed : [];
                    setActionResult({ action: "unpay", processed, skipped, failed });
                    setFeedbackMessage(`${processed.length} commissions reopened. ${skipped.length} skipped. ${failed.length} failed.`);
                    setFeedbackAction(null);
                    const processedIds = new Set(processed.map((p) => `ev:${String(p.id)}`));
                    setSelected((prev) => prev.filter((id) => !processedIds.has(String(id))));
                  }
                  await runFetch();
                } catch (e) {
                  console.error(e);
                  const failed = e?.response?.data?.failed || [{ id: null, reason: e?.response?.data?.message || "Bulk action failed", label: "Request failed" }];
                  setActionResult({ action: bulkDialogMode || "bulk", processed: [], skipped: [], failed });
                } finally {
                  if (filterExcludesPaidAfterPay() && (bulkDialogMode === "pay" || bulkDialogMode === "pay_export_xlsx")) {
                    setFeedbackMessage("Commissions were marked paid. They may no longer appear because your current filter excludes paid rows.");
                    setFeedbackAction({ label: "Show Paid", kind: "show_paid" });
                  }
                  if (filterExcludesUnpaidAfterUnpay() && bulkDialogMode === "unpay") {
                    setFeedbackMessage("Commissions were reopened. They may no longer appear because your current filter excludes unpaid rows.");
                    setFeedbackAction({ label: "Show Unpaid", kind: "show_unpaid" });
                  } else if (hasExtraNarrowingFilters() && (bulkDialogMode === "pay" || bulkDialogMode === "pay_export_xlsx" || bulkDialogMode === "unpay")) {
                    setFeedbackAction({ label: "Clear Filter", kind: "clear_filters" });
                  }
                  setBulkProcessing(false);
                  setBulkDialogMode(null);
                }
              }}
            />
            <Dialog open={reversePickerOpen} onClose={() => setReversePickerOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>Reverse commission payments</DialogTitle>
              <DialogContent dividers sx={{ display: "grid", gap: 1 }}>
                <Typography variant="body2">selected commission rows: {selectedRows.length} · active linked payments: {selectedLinkedPayments.length} · selected amount to reverse: {currency(selectedLinkedPayments.filter((p) => reversePaymentIds.includes(Number(p.id))).reduce((n,p)=>n+Number(p.amount||0),0))}</Typography>
                {selectedLinkedPayments.map((p) => (
                  <FormControlLabel key={`rp-${p.id}`} control={<Checkbox checked={reversePaymentIds.includes(Number(p.id))} onChange={(e) => setReversePaymentIds((prev) => e.target.checked ? [...new Set([...prev, Number(p.id)])] : prev.filter((x) => Number(x) !== Number(p.id)))} />} label={`Payment #${p.id} · ${p.repName || '—'} · Order ${p.orderNumber || '—'} · ${p.invoiceNumber || '—'} · ${currency(p.amount)} · ${p.reference || '—'}`} />
                ))}
                <TextField label="Reason for reversal" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} multiline minRows={2} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setReversePickerOpen(false)}>Cancel</Button>
                <Button variant="contained" color="warning" disabled={!reversePaymentIds.length || !reverseReason.trim() || bulkProcessing} onClick={async () => {
                  setReversePickerOpen(false);
                  setBulkDialogMode("unpay");
                  await (async()=>{})();
                  // trigger existing bulk flow with chosen ids/reason
                  try {
                    if (bulkProcessing) return;
                    setBulkProcessing(true);
                    const res = await unpayCommissions({ paymentIds: reversePaymentIds, reason: reverseReason });
                    const data = res?.data || {};
                    const processed = Array.isArray(data.processed) ? data.processed : [];
                    const skipped = Array.isArray(data.skipped) ? data.skipped : [];
                    const failed = Array.isArray(data.failed) ? data.failed : [];
                    setActionResult({ action: "unpay", processed, skipped, failed });
                    setFeedbackMessage(`${processed.length} payments reversed. ${skipped.length} skipped. ${failed.length} failed.`);
                    await runFetch();
                  } finally { setBulkProcessing(false); }
                }}>Reverse selected payments</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={!!actionResult} onClose={() => setActionResult(null)} fullWidth maxWidth="sm">
              <DialogTitle>Bulk action result</DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2">
                  {(actionResult?.processed || []).length} processed, {(actionResult?.skipped || []).length} skipped, {(actionResult?.failed || []).length} failed.
                </Typography>
                {!!(actionResult?.processed || []).length && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Paid/Reopened Rows</Typography>
                    {(actionResult.processed || []).map((p, i) => <Typography key={`p-${i}`} variant="caption" display="block">{`Paid ${currency(p.amount || p.amountPaidNow || 0)} for ${p.repName || `Rep #${p.repUserId || "—"}`} · Order ${p.orderNumber || p.orderId || "—"} · ${p.invoiceNumber || `INV-${p.invoiceId || "—"}`}`}</Typography>)}
                  </Box>
                )}
                {!!(actionResult?.skipped || []).length && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Skipped</Typography>
                    {(actionResult.skipped || []).map((s, i) => <Typography key={`s-${i}`} variant="caption" display="block">#{s.id || "—"} · {s.label || s.reason}</Typography>)}
                  </Box>
                )}
                {!!(actionResult?.failed || []).length && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Failed</Typography>
                    {(actionResult.failed || []).map((f, i) => <Typography key={`f-${i}`} variant="caption" display="block">#{f.id || "—"} · {f.label || f.reason}</Typography>)}
                  </Box>
                )}
              </DialogContent>
              <DialogActions><Button onClick={() => setActionResult(null)}>Close</Button></DialogActions>
            </Dialog>
            <Snackbar
              open={!!feedbackMessage}
              autoHideDuration={6000}
              onClose={() => { setFeedbackMessage(""); setFeedbackAction(null); }}
              message={feedbackMessage}
              action={feedbackAction ? (
                <Button
                  color="secondary"
                  size="small"
                  onClick={() => {
                    if (feedbackAction.kind === "show_paid") { setPaidStatus("paid"); setCommissionStatus("paid"); }
                    else if (feedbackAction.kind === "show_unpaid") { setPaidStatus("unpaid"); setCommissionStatus("unpaid"); }
                    else if (feedbackAction.kind === "clear_filters") {
                      setPaidStatus(""); setCommissionStatus("both"); setReadyFilter("all");
                      setDateFrom(""); setDateTo(""); setInvoiceDatePaidFrom(""); setInvoiceDatePaidTo(""); setCommissionDatePaidFrom(""); setCommissionDatePaidTo("");
                    }
                    setFeedbackMessage("");
                    setFeedbackAction(null);
                  }}
                >
                  {feedbackAction.label}
                </Button>
              ) : undefined}
            />
            <Dialog open={!!historyRow} onClose={() => setHistoryRow(null)} fullWidth maxWidth="sm">
              <DialogTitle>Payment notes & history</DialogTitle>
              <DialogContent dividers>
                {historyRow && (
                  <>
                    <Typography variant="body2"><b>{historyRow.repDisplayName || `Rep #${historyRow.repUserId || "—"}`}</b> · Order {historyRow.orderNumber || historyRow.OrderId || historyRow.orderId || "—"} · {historyRow.invoiceNumber || `INV-${historyRow.invoiceId || "—"}`}</Typography>
                    <Typography variant="caption" display="block">Parent payout: {historyRow.commissionPayoutEventId || "—"} · Status: {historyRow.paymentStatusLabel || historyRow.paymentStatus || "—"}</Typography>
                    <Typography variant="caption" display="block">Paid to date: {currency(historyRow.paidToDate || 0)} · {historyRow.paymentProgressLabel || "No progress yet"}</Typography>
                    {!!(historyRow.paymentAnomalies || []).length && <Alert severity="warning" sx={{ mt: 1 }}>{historyRow.paymentAnomalies.join(", ")}</Alert>}
                    <Box sx={{ mt: 1 }}>
                      {(historyRow.paymentHistory || []).map((ev, i) => (
                        <Typography key={`hev-${i}`} variant="caption" display="block">
                          {String(ev.eventType || "").toUpperCase()} · {ev.eventDate ? new Date(ev.eventDate).toLocaleString() : "—"} · {currency(ev.amount || 0)} · {ev.reference || "—"} · {ev.note || "—"}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
              </DialogContent>
              <DialogActions><Button onClick={() => setHistoryRow(null)}>Close</Button></DialogActions>
            </Dialog>

            {financeUser && selectedNotReadyCount > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                {selectedNotReadyCount} selected row(s) are not ready to pay. Review the "Ready to Pay" column for details.
              </Typography>
            )}

            {financeUser && selected.length > 0 && selectedUnpayableRows.length === 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                Un-pay requires paid/posted commission rows that can be reopened.
              </Typography>
            )}



            {/* TABLE */}
            <UIDataGridTable
              rows={visibleRows}
              columns={columns}
              minWidth={commissionTableMinWidth}
              height={600}
              rowHeight={52}
              density="compact"
              pageSizeOptions={[10, 25, 50]}
              loading={loading}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(ids) => setSelected(Array.from(ids || []))}
              rowSelectionModel={selected}
              getRowId={getRowKey}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f4f6fa",
                },
                "& .MuiDataGrid-columnHeader": {
                  py: 0.75,
                  alignItems: "center",
                  alignContent: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 800,
                  margin: "0 !important",
                  padding: "0 !important",
                },
                "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent": {
                  alignItems: "center",
                  alignContent: "center",
                  margin: "0 !important",
                  padding: "0 !important",
                },
                "& .MuiDataGrid-row, & .MuiDataGrid-cell": {
                  minHeight: "52px !important",
                  maxHeight: "52px !important",
                  height: "52px !important",
                },
                "& .MuiDataGrid-cell": {
                  py: 0.25,
                  alignContent: "center",
                },
                "& .MuiDataGrid-cellContent": {
                  alignItems: "center",
                  alignContent: "center",
                },
                "& .MuiDataGrid-columnHeader[data-field='__check__'], & .MuiDataGrid-cell[data-field='__check__']": {
                  width: "50px !important",
                  minWidth: "50px !important",
                  maxWidth: "50px !important",
                  paddingLeft: "0 !important",
                  paddingRight: "0 !important",
                  justifyContent: "center",
                  alignItems: "center",
                  alignContent: "center",
                },
                "& .MuiDataGrid-cell[data-field='_actions']": {
                  justifyContent: "center",
                },
                "& .MuiDataGrid-columnHeader[data-field='_actions'] .MuiDataGrid-columnHeaderTitleContainer":
                  {
                    justifyContent: "center !important",
                  },
              }}
            />
              </>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>Ready to run commissions reporting</Typography>
                <Typography variant="body2">
                  This report supports filtering by rep, client, invoice dates, invoice paid dates, paid statuses, and invoice/bill/commission status. Use filters above and click <strong>Get Report</strong>.
                </Typography>
              </Paper>
            )}
            </Box>
          </UITableShell>
        </Box>
      </div>
      <Dialog open={policyDialogOpen} onClose={() => setPolicyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Commission Readiness Policy</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Control which rules must pass before a commission row is marked as ready to pay.
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={!!readinessPolicy.requireInvoicePaid} onChange={setPolicyField('requireInvoicePaid')} />}
              label="Require invoice marked paid"
            />
            <FormControlLabel
              control={<Checkbox checked={!!readinessPolicy.requireInvoiceFinalized} onChange={setPolicyField('requireInvoiceFinalized')} />}
              label="Require finalized invoice artifact"
            />
            <FormControlLabel
              control={<Checkbox checked={!!readinessPolicy.requireMarginFinalized} onChange={setPolicyField('requireMarginFinalized')} />}
              label="Require finalized margin inputs"
            />
            <FormControlLabel
              control={<Checkbox checked={!!readinessPolicy.requireAssignedPlan} onChange={setPolicyField('requireAssignedPlan')} />}
              label="Require assigned commission plan"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPolicyDefaults} disabled={policySaving}>Reset defaults</Button>
          <Button onClick={() => setPolicyDialogOpen(false)} color="inherit" disabled={policySaving}>Cancel</Button>
          <Button onClick={handleSavePolicy} variant="contained" disabled={policySaving}>
            {policySaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit dialog */}
      <EditCommissionDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        row={editRow}
        instanceUniqueId={instanceUniqueId}
        instanceId={instanceId}
        onSaved={(updated) => {
          // update in-place
          const updKey = (r) =>
            String(r.orderId) === String(updated.orderId) &&
            String(r.invoiceNumber ?? r.invoiceId ?? "") === String(updated.invoiceNumber ?? updated.invoiceId ?? "");
          const next = rows.map((r) => (updKey(r) ? { ...r, ...updated } : r));
          setRawRows(next);
        }}
      />
    </div>
  );
}


